import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ShoppingCart, ArrowLeft, Search, Filter, Plus, Package, Truck,
  AlertCircle, CheckCircle, Clock, TrendingUp, Building, Eye, Edit,
  Download, BarChart3, PackageOpen, Pill, Heart, Upload, FileText,
  X, Save, RefreshCw, Trash2, ChevronDown, ChevronUp, Receipt, Link,
  FlaskConical, Link2, PackagePlus, Unlink, ExternalLink, Zap, Globe
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import * as XLSX from "xlsx";
import Inventory from "./Inventory";
import PurchaseOrders from "./PurchaseOrders";
import DeliveryNotes from "./DeliveryNotes";
import UserMenu from "../components/UserMenu";
import { apiFetch } from "../utils/api";

// Kept only for the <a href={API + "/api/xero/connect"}> links — a
// top-level navigation cannot carry an Authorization header, so that
// endpoint stays unauthenticated by design. Every fetch goes through
// apiFetch, which adds the Bearer token itself.
const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Purchasing hub — the largest staff page. Tabbed container for the
 * whole procurement pipeline:
 *   inventory      → <Inventory/>      stock items & stock levels
 *   orders         → <PurchaseOrders/> POs raised against suppliers
 *   delivery-notes → <DeliveryNotes/>  goods received against POs
 *   invoices       → inline            supplier invoice capture: PDF
 *                                      upload + /purchasing/invoices/parse,
 *                                      line-item editing, price updates,
 *                                      push to Xero
 *   suppliers      → <SupplierDatabase/> supplier CRUD, inventory matching,
 *                                      Xero contact import/export
 *   analytics      → <PurchasingAnalytics/> spend dashboards (XLSX export
 *                                      via the xlsx npm package)
 *
 * Two external integrations meet here: Xero (OAuth connect banner,
 * invoice POST, contact sync — see backend/routes/xero.py) and the
 * surgeons DB (hubs are reused as "purchaser" delivery addresses via
 * /purchasing/match-hub).
 *
 * All backend calls go through `utils/api.js` (apiFetch), which adds
 * the Bearer token and redirects to '/' on 401. The only raw URLs left
 * are the <a href> Xero connect links — browser navigations that cannot
 * carry a token, which is why the backend leaves /api/xero/connect open.
 */

// ── Xero connection hook ─────────────────────────────────────────────────────
function useXeroStatus() {
  const [status, setStatus] = useState(null); // null=loading, { connected, tenant_name }

  const check = useCallback(function() {
    apiFetch("/api/xero/status")
      .then(function(r) { return r.json(); })
      .then(function(d) { setStatus(d); })
      .catch(function() { setStatus({ connected: false }); });
  }, []);

  useEffect(check, [check]);

  const disconnect = async function() {
    await apiFetch("/api/xero/disconnect", { method: "POST" });
    check();
  };

  return { status, check, disconnect };
}

// ── Xero connect banner ───────────────────────────────────────────────────────
const XeroConnectBanner = ({ status, onDisconnect }) => {
  if (!status) {
    return (
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-400 animate-pulse">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Checking Xero connection…
      </div>
    );
  }
  if (status.connected) {
    return (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
        <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
          <Zap className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-800">Connected to Xero</p>
          <p className="text-xs text-emerald-600 truncate">{status.tenant_name}</p>
        </div>
        <button onClick={onDisconnect}
          className="text-xs text-gray-400 hover:text-red-600 border border-gray-200 hover:border-red-200 rounded-lg px-3 py-1.5">
          Disconnect
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
      <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center shrink-0">
        <Globe className="h-4 w-4 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-blue-800">Xero not connected</p>
        <p className="text-xs text-blue-600">Connect to post invoices directly to your Xero account</p>
      </div>
      <a href={API + "/api/xero/connect"}
        className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 font-medium">
        <ExternalLink className="h-3 w-3" /> Connect Xero
      </a>
    </div>
  );
};

function useHubs() {
  const [hubs, setHubs] = useState([]);
  useEffect(function() {
    apiFetch("/api/surgeons-db/hubs")
      .then(function(r) { return r.json(); })
      .then(function(d) { setHubs(Array.isArray(d) ? d : []); })
      .catch(function() {});
  }, []);
  return hubs;
}

// ?? Supplier Match Panel ??????????????????????????????????????????????????????
const SupplierMatchPanel = ({ supplierName, supplierAddress, onLinked, onSkip }) => {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState(supplierName || "");
  const [newPostcode, setNewPostcode] = useState("");
  const [newAddress, setNewAddress] = useState(supplierAddress || "");

  useEffect(function() {
    if (!supplierName) return;
    setLoading(true);
    var pc = "";
    var pcMatch = (supplierAddress || "").match(/[A-Z]{1,2}\d[\dA-Z]?\s*\d[A-Z]{2}/i);
    if (pcMatch) pc = pcMatch[0];
    var params = new URLSearchParams({ q: supplierName });
    if (pc) params.set("postcode", pc);
    apiFetch("/api/purchasing/suppliers?" + params.toString())
      .then(function(r) { return r.json(); })
      .then(function(d) { setMatches(d.items || []); })
      .catch(function() { setMatches([]); })
      .finally(function() { setLoading(false); });
  }, [supplierName, supplierAddress]);

  const handleCreate = async function() {
    setCreating(true);
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, 15000);
    try {
      var pcMatch2 = newAddress.match(/[A-Z]{1,2}\d[\dA-Z]?\s*\d[A-Z]{2}/i);
      var pc2 = newPostcode || (pcMatch2 ? pcMatch2[0] : "");
      var res = await apiFetch("/api/purchasing/suppliers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName, address: newAddress, postcode: pc2 }),
        signal: controller.signal
      });
      var text = await res.text();
      var data;
      try { data = JSON.parse(text); } catch(_) {
        throw new Error("Server returned non-JSON: " + text.slice(0, 200));
      }
      if (!res.ok) throw new Error((data && data.detail) ? JSON.stringify(data.detail) : "Failed to create supplier (" + res.status + ")");
      onLinked(data);
    } catch(e) {
      alert(e.name === "AbortError" ? "Request timed out - check backend is running." : e.message);
    } finally {
      clearTimeout(timer);
      setCreating(false);
    }
  };

  if (loading) return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-600 flex items-center gap-2">
      <RefreshCw className="h-4 w-4 animate-spin" /> Searching supplier database...
    </div>
  );

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-amber-800 text-sm flex items-center gap-2">
          <Building className="h-4 w-4" /> Supplier: {supplierName}
        </h4>
        <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
      </div>

      {matches && matches.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-amber-700">Found {matches.length} existing supplier(s). Link to one?</p>
          {matches.map(function(m) {
            return (
              <div key={m.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.address || m.postcode || "No address"}</p>
                </div>
                <button
                  onClick={function() { onLinked(m); }}
                  className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center gap-1"
                >
                  <Link className="h-3 w-3" /> Link
                </button>
              </div>
            );
          })}
          <p className="text-xs text-amber-600 pt-1">Or create as new supplier:</p>
        </div>
      ) : (
        <p className="text-xs text-amber-700">No existing supplier matched. Create a new one?</p>
      )}

      <div className="space-y-2 pt-1">
        <input value={newName} onChange={function(e) { setNewName(e.target.value); }}
          placeholder="Supplier name" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5" />
        <input value={newAddress} onChange={function(e) { setNewAddress(e.target.value); }}
          placeholder="Address" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5" />
        <input value={newPostcode} onChange={function(e) { setNewPostcode(e.target.value); }}
          placeholder="Postcode (auto-detected if blank)" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5" />
        <button onClick={handleCreate} disabled={creating || !newName}
          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2">
          {creating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create New Supplier
        </button>
      </div>
    </div>
  );
};

// ?? Hub Match Banner ??????????????????????????????????????????????????????????
const HubMatchBanner = ({ purchaserAddress, onHubSelected }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedHub, setSelectedHub] = useState(null);

  useEffect(function() {
    if (!purchaserAddress) return;
    setLoading(true);
    apiFetch("/api/purchasing/match-hub?address=" + encodeURIComponent(purchaserAddress))
      .then(function(r) { return r.json(); })
      .then(function(d) { setResult(d); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, [purchaserAddress]);

  if (!purchaserAddress) return null;
  if (loading) return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-600 flex items-center gap-2">
      <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Matching purchaser address to hub...
    </div>
  );
  if (!result || (result.clinics.length === 0 && result.hubs.length === 0)) return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-500">
      Purchaser address: <span className="font-medium text-gray-700">{purchaserAddress}</span> - no clinic match found.
    </div>
  );

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
      <p className="text-xs font-semibold text-green-800">Purchaser address matched to clinic(s):</p>
      <div className="space-y-1">
        {result.clinics.map(function(c) {
          var hub = result.hubs.find(function(h) { return (c.hub_ids || []).includes(h.id); });
          return (
            <div key={c.id} className="flex items-center justify-between bg-white border border-green-200 rounded-lg px-3 py-2">
              <div>
                <p className="text-xs font-medium text-gray-800">{c.name}</p>
                <p className="text-xs text-gray-500">{hub ? "Hub: " + hub.name : "No hub"} - {c.postcode || ""}</p>
              </div>
              <button
                onClick={function() {
                  var hubObj = hub || null;
                  setSelectedHub(hubObj);
                  onHubSelected(hubObj, c);
                }}
                className={"text-xs px-2.5 py-1 rounded-lg " + (selectedHub && hub && selectedHub.id === (hub ? hub.id : "") ? "bg-green-600 text-white" : "bg-green-100 text-green-700 hover:bg-green-200")}
              >
                {selectedHub && hub && selectedHub.id === hub.id ? "Selected" : "Use this hub"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Inventory search hook ─────────────────────────────────────────────────────

function useInventorySearch(query) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);
  useEffect(function() {
    if (!query || query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function() {
      apiFetch("/api/inventory/items?search=" + encodeURIComponent(query.trim()) + "&limit=6")
        .then(function(r) { return r.ok ? r.json() : { items: [] }; })
        .then(function(d) { setResults(d.items || []); setSearching(false); })
        .catch(function() { setSearching(false); });
    }, 350);
    return function() { clearTimeout(timerRef.current); };
  }, [query]);
  return { results, searching };
}

// ── Per-line inventory match row ──────────────────────────────────────────────

var INV_TYPES = ["Drug", "Consumable", "Equipment", "Fluid", "Food", "Other"];

var UNIT_TYPES_P = ["Bottle", "Box", "Packet", "Tray", "Tub", "Tube", "Bag", "Vial", "Ampoule", "Canister", "Inhaler", "Sachet", "Other"];

var UNIT_SUBTYPE_OPTIONS_P = {
  Bottle:   ["ml", "L", "mg", "g", "tablet", "capsule", "other"],
  Vial:     ["ml", "mg", "IU", "other"],
  Ampoule:  ["ml", "mg", "other"],
  Box:      ["tablet", "capsule", "sachet", "ampoule", "vial", "other"],
  Packet:   ["tablet", "capsule", "sachet", "g", "other"],
  Tray:     ["tablet", "capsule", "vial", "ampoule", "other"],
  Tub:      ["tablet", "capsule", "g", "ml", "other"],
  Tube:     ["g", "ml", "other"],
  Bag:      ["ml", "L", "g", "other"],
  Canister: ["ml", "g", "dose", "other"],
  Inhaler:  ["dose", "mcg", "other"],
  Sachet:   ["g", "ml", "other"],
  Other:    ["ml", "L", "mg", "g", "tablet", "capsule", "other"],
};

function unitSummaryP(f) {
  if (!f.unit_type) return "";
  var type = f.unit_type.toLowerCase();
  if (f.unit_has_subunits && f.unit_subqty && f.unit_subtype) {
    return "1 " + type + " contains " + f.unit_subqty + " " + f.unit_subtype;
  }
  if (f.unit_dispensing) return "Dispensing unit: " + f.unit_dispensing;
  return "1 " + type;
}

function UnitSetupP({ item, setField, inp }) {
  var subtypeOptions = (UNIT_SUBTYPE_OPTIONS_P[item.unit_type] || ["other"]);
  var dispensingOptions = item.unit_has_subunits && item.unit_subtype
    ? [item.unit_type, item.unit_subtype]
    : (item.unit_type ? [item.unit_type] : []);
  var summary = unitSummaryP(item);

  return (
    <div className="space-y-3 col-span-2 md:col-span-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit Setup</p>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        <div>
          <label className="block text-xs text-gray-400 mb-0.5">Unit Type</label>
          <select value={item.unit_type || ""}
            onChange={function(e) {
              setField("unit_type", e.target.value);
              setField("unit_has_subunits", false);
              setField("unit_subqty", "");
              setField("unit_subtype", "");
              setField("unit_dispensing", "");
            }}
            className={inp}>
            <option value="">— select —</option>
            {UNIT_TYPES_P.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
          </select>
        </div>
        {item.unit_type && (
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
              <input type="checkbox" checked={!!item.unit_has_subunits}
                onChange={function(e) {
                  setField("unit_has_subunits", e.target.checked);
                  if (!e.target.checked) { setField("unit_subqty", ""); setField("unit_subtype", ""); setField("unit_dispensing", ""); }
                }}
                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600"
              />
              Has sub-units?
            </label>
          </div>
        )}
      </div>
      {item.unit_type && item.unit_has_subunits && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 pl-3 border-l-2 border-purple-100">
          <div>
            <label className="block text-xs text-gray-400 mb-0.5">No. of sub-units</label>
            <input type="number" min="0" step="any" value={item.unit_subqty || ""}
              onChange={function(e) { setField("unit_subqty", e.target.value); }}
              placeholder="e.g. 5" className={inp} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-0.5">Sub-unit type</label>
            <select value={item.unit_subtype || ""}
              onChange={function(e) { setField("unit_subtype", e.target.value); setField("unit_dispensing", ""); }}
              className={inp}>
              <option value="">— select —</option>
              {subtypeOptions.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-0.5">Dispensing unit</label>
            <select value={item.unit_dispensing || ""}
              onChange={function(e) { setField("unit_dispensing", e.target.value); }}
              className={inp}>
              <option value="">— select —</option>
              {dispensingOptions.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
            </select>
          </div>
        </div>
      )}
      {item.unit_type && !item.unit_has_subunits && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <div>
            <label className="block text-xs text-gray-400 mb-0.5">Dispensing unit</label>
            <select value={item.unit_dispensing || ""}
              onChange={function(e) { setField("unit_dispensing", e.target.value); }}
              className={inp}>
              <option value="">— select —</option>
              {dispensingOptions.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
            </select>
          </div>
        </div>
      )}
      {summary && (
        <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded px-3 py-2">
          <span className="text-xs text-purple-800 font-medium">{summary}</span>
        </div>
      )}
    </div>
  );
}

const InvMatchRow = ({ lineItem, supplierLinked, match, onMatch }) => {
  // match shape: null | { status: 'matched'|'new'|'skip', item }
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // "search" | "create"
  const [searchQ, setSearchQ] = useState(lineItem.supplier_code || lineItem.description || "");
  const { results, searching } = useInventorySearch(searchQ);

  // Separate auto-search by supplier code so we can tag those results
  const { results: codeResults } = useInventorySearch(lineItem.supplier_code || "");

  // Auto-match when an exact supplier code match is found and nothing decided yet
  // NOTE: handleConfirmMatch is defined further down but hoisted via var — safe to call here
  useEffect(function() {
    if (match) return;                          // already decided
    if (!lineItem.supplier_code) return;        // no code to match on
    if (codeResults.length === 0) return;
    var exact = codeResults.find(function(item) {
      return (item.suppliers || []).some(function(s) {
        return (s.supplier_code || "").toLowerCase() === lineItem.supplier_code.toLowerCase();
      });
    });
    if (exact) {
      handleConfirmMatch(exact, true);
    }
  }, [codeResults]);

  // Keep a ref so handleCreate always sees the latest supplierLinked value
  const supplierLinkedRef = useRef(supplierLinked);
  useEffect(function() { supplierLinkedRef.current = supplierLinked; }, [supplierLinked]);

  // ── Item-level fields (generic, catalogue data) ──
  const [newItem, setNewItem] = useState({
    item_type: "",
    nominal_code: lineItem.nominal_code || "",
    description: lineItem.description || "",
    generic_name: "",
    unit_type: "", unit_has_subunits: false, unit_subqty: "", unit_subtype: "", unit_dispensing: "",
    qty_in_stock: "0", par_level: "0", reorder_point: "0"
  });

  // ── Supplier child fields (supplier-specific / line-level data) ──
  const [supChild, setSupChild] = useState({
    supplier_code: lineItem.supplier_code || "",   // supplier's product code
    price: lineItem.unit_price || "",              // unit price from invoice
    outer_quantity: "",
    outer_quantity_type: "",
    selling_units: "",
    cost_per_selling_unit: "",
    min_order_qty: "",
    max_order_qty: "",
    notes: ""
  });

  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  var inp = "w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-300 focus:outline-none";
  var setNF = function(k, v) { setNewItem(function(p) { return Object.assign({}, p, { [k]: v }); }); };
  var setSC = function(k, v) { setSupChild(function(p) { return Object.assign({}, p, { [k]: v }); }); };

  // ── Upsert supplier child onto an existing inventory item ─────────────────
  // Finds a matching child by supplier_code + supplier_id and updates it,
  // or adds a new child if none found. Silently ignores errors.
  var upsertSupplierChild = async function(invItem, supChildData, sup) {
    if (!invItem || !invItem.id) return invItem;
    // Only proceed if we have at least a supplier code or supplier identity
    if (!supChildData.supplier_code && !sup) return invItem;

    var existingSuppliers = (invItem.suppliers || []).slice();
    var supId   = sup ? (sup.id || "") : "";
    var supName = sup ? (sup.name || "") : "";
    var code    = supChildData.supplier_code || "";

    // Find matching child: same supplier_id (if known) OR same supplier_code
    var matchIdx = existingSuppliers.findIndex(function(s) {
      if (supId && s.supplier_id && s.supplier_id === supId) return true;
      if (code && s.supplier_code && s.supplier_code.toLowerCase() === code.toLowerCase()) return true;
      return false;
    });

    var updatedChild;
    if (matchIdx >= 0) {
      // Merge invoice data into existing child — only overwrite non-empty fields
      var existing = existingSuppliers[matchIdx];
      updatedChild = Object.assign({}, existing, {
        supplier_id:   supId   || existing.supplier_id   || "",
        supplier_name: supName || existing.supplier_name || "",
        supplier_code: code    || existing.supplier_code || "",
        price:         supChildData.price         || existing.price         || "",
        outer_quantity:      supChildData.outer_quantity      || existing.outer_quantity      || "",
        outer_quantity_type: supChildData.outer_quantity_type || existing.outer_quantity_type || "",
        selling_units:       supChildData.selling_units       || existing.selling_units       || "",
        cost_per_selling_unit: supChildData.cost_per_selling_unit || existing.cost_per_selling_unit || "",
        min_order_qty: supChildData.min_order_qty || existing.min_order_qty || "",
        max_order_qty: supChildData.max_order_qty || existing.max_order_qty || "",
        notes:         supChildData.notes         || existing.notes         || "",
      });
      existingSuppliers[matchIdx] = updatedChild;
    } else {
      // Add as new child (non-default unless it's the only one)
      updatedChild = {
        id: "",
        supplier_id:   supId,
        supplier_name: supName,
        supplier_code: code,
        price:         supChildData.price         || "",
        outer_quantity:      supChildData.outer_quantity      || "",
        outer_quantity_type: supChildData.outer_quantity_type || "",
        selling_units:       supChildData.selling_units       || "",
        cost_per_selling_unit: supChildData.cost_per_selling_unit || "",
        min_order_qty: supChildData.min_order_qty || "",
        max_order_qty: supChildData.max_order_qty || "",
        is_default: existingSuppliers.length === 0,
        notes: supChildData.notes || "",
      };
      existingSuppliers.push(updatedChild);
    }

    try {
      var res = await apiFetch("/api/inventory/items/" + invItem.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suppliers: existingSuppliers })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch(_) {}
    return invItem; // return original if patch failed
  };

  var handleConfirmMatch = async function(item, autoMatched) {
    var sup = supplierLinkedRef.current;
    var updated = await upsertSupplierChild(item, supChild, sup);
    onMatch({ status: "matched", item: updated || item, autoMatched: !!autoMatched });
    setOpen(false);
  };

  var handleSkip = function() {
    onMatch({ status: "skip", item: null });
    setOpen(false);
  };

  var handleCreate = async function() {
    if (!newItem.description.trim()) { setCreateErr("Description required."); return; }
    setCreating(true); setCreateErr(null);
    try {
      var sup = supplierLinkedRef.current;
      var childEntry = sup ? {
        id: "",
        supplier_id: sup.id || "",
        supplier_name: sup.name || "",
        supplier_code: supChild.supplier_code,
        price: supChild.price,
        outer_quantity: supChild.outer_quantity,
        outer_quantity_type: supChild.outer_quantity_type,
        selling_units: supChild.selling_units,
        cost_per_selling_unit: supChild.cost_per_selling_unit,
        min_order_qty: supChild.min_order_qty,
        max_order_qty: supChild.max_order_qty,
        is_default: true,
        notes: supChild.notes
      } : null;

      var payload = {
        item_type: newItem.item_type,
        nominal_code: newItem.nominal_code,
        product_code: "",               // product_code lives on supplier child, not item
        description: newItem.description,
        generic_name: newItem.generic_name,
        conc_qty: newItem.conc_qty,
        conc_unit: newItem.conc_unit,
        conc_format: newItem.conc_format,
        qty_in_stock: parseFloat(newItem.qty_in_stock) || 0,
        par_level: parseFloat(newItem.par_level) || 0,
        reorder_point: parseFloat(newItem.reorder_point) || 0,
        suppliers: childEntry ? [childEntry] : []
      };

      var res = await apiFetch("/api/inventory/items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Create failed");
      onMatch({ status: "matched", item: data, justCreated: true });
      setOpen(false);
    } catch(err) {
      setCreateErr(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Status badge
  var badge = null;
  if (match) {
    if (match.status === "matched") {
      var matchLabel = match.justCreated ? "Created: " : match.autoMatched ? "Auto-matched: " : "Linked: ";
      badge = (
        <span className={"inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium " + (match.autoMatched ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700")}>
          <Link2 className="h-3 w-3" />
          {matchLabel}{match.item.description}
          <button onClick={function() { onMatch(null); setOpen(true); }}
            className="ml-1 opacity-60 hover:opacity-100" title="Change"><X className="h-2.5 w-2.5" /></button>
        </span>
      );
    } else if (match.status === "skip") {
      badge = (
        <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
          <Unlink className="h-3 w-3" /> Not linked
          <button onClick={function() { onMatch(null); setOpen(true); }}
            className="ml-1 hover:text-gray-700" title="Change"><X className="h-2.5 w-2.5" /></button>
        </span>
      );
    }
  }

  if (!open && match) {
    return (
      <tr className="bg-gray-50/60 border-b border-gray-100">
        <td colSpan={99} className="px-3 py-1">{badge}</td>
      </tr>
    );
  }

  if (!open) {
    return (
      <tr className="bg-blue-50/30 border-b border-gray-100">
        <td colSpan={99} className="px-3 py-1">
          <button onClick={function() { setOpen(true); }}
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded px-2 py-0.5">
            <PackagePlus className="h-3.5 w-3.5" /> Link or add to inventory
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-purple-100">
      <td colSpan={99} className="px-3 py-2 bg-purple-50/40">
        <div className="space-y-2">
          {/* Mode tabs */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button onClick={function() { setMode("search"); }}
                className={"text-xs px-2.5 py-1 rounded font-medium " + (mode === "search" ? "bg-purple-600 text-white" : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50")}>
                <Search className="h-3 w-3 inline mr-1" />Match existing
              </button>
              <button onClick={function() { setMode("create"); }}
                className={"text-xs px-2.5 py-1 rounded font-medium " + (mode === "create" ? "bg-purple-600 text-white" : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50")}>
                <Plus className="h-3 w-3 inline mr-1" />Add new item
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleSkip}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                <Unlink className="h-3 w-3" /> Skip
              </button>
              <button onClick={function() { setOpen(false); }}
                className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>

          {/* Search mode */}
          {mode === "search" && (
            <div className="space-y-1.5">
              {/* Supplier code auto-matches shown first */}
              {lineItem.supplier_code && codeResults.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-blue-600 font-medium flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Matched by supplier code <span className="font-mono bg-blue-50 px-1 rounded">{lineItem.supplier_code}</span>
                  </p>
                  {codeResults.map(function(item) {
                    var matchedChild = (item.suppliers || []).find(function(s) {
                      return (s.supplier_code || "").toLowerCase() === (lineItem.supplier_code || "").toLowerCase();
                    });
                    return (
                      <div key={item.id}
                        className="flex items-center justify-between bg-blue-50 border border-blue-200 hover:border-blue-400 rounded-lg px-3 py-1.5 cursor-pointer group"
                        onClick={function() { handleConfirmMatch(item); }}>
                        <div>
                          <span className="text-xs font-medium text-gray-800">{item.description}</span>
                          <span className="ml-2 text-xs text-gray-400">{item.item_type}{unitSummaryP(item) ? " · " + unitSummaryP(item) : ""}{item.generic_name ? " · " + item.generic_name : ""}</span>
                          {matchedChild && (
                            <span className="ml-2 text-xs text-blue-600 font-mono">code: {matchedChild.supplier_code}</span>
                          )}
                        </div>
                        <span className="text-xs text-blue-600 group-hover:text-blue-800 shrink-0 ml-3 flex items-center gap-1">
                          <CheckCircle className="h-3.5 w-3.5" /> Match
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Manual description search */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input value={searchQ} onChange={function(e) { setSearchQ(e.target.value); }}
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-300 focus:outline-none"
                  placeholder="Search by description, name, or supplier code…" />
              </div>
              {searching && <p className="text-xs text-gray-400 animate-pulse">Searching…</p>}
              {!searching && results.length === 0 && searchQ.length >= 2 && (
                <p className="text-xs text-gray-400">No matching items found.
                  <button onClick={function() { setMode("create"); }} className="ml-1 text-purple-600 hover:underline">Add new item?</button>
                </p>
              )}
              {results.length > 0 && (
                <div className="space-y-1">
                  {results.map(function(item) {
                    var codeMatch = (item.suppliers || []).some(function(s) {
                      return searchQ.length >= 2 && (s.supplier_code || "").toLowerCase().includes(searchQ.toLowerCase());
                    });
                    return (
                      <div key={item.id}
                        className="flex items-center justify-between bg-white border border-gray-200 hover:border-purple-300 rounded-lg px-3 py-1.5 cursor-pointer group"
                        onClick={function() { handleConfirmMatch(item); }}>
                        <div>
                          <span className="text-xs font-medium text-gray-800">{item.description}</span>
                          <span className="ml-2 text-xs text-gray-400">{item.item_type}{unitSummaryP(item) ? " · " + unitSummaryP(item) : ""}{item.generic_name ? " · " + item.generic_name : ""}</span>
                          {codeMatch && (
                            <span className="ml-2 text-xs text-blue-500 font-mono">
                              {(item.suppliers || []).filter(function(s) { return (s.supplier_code || "").toLowerCase().includes(searchQ.toLowerCase()); }).map(function(s) { return s.supplier_code; }).join(", ")}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-purple-600 group-hover:text-purple-800 shrink-0 ml-3 flex items-center gap-1">
                          <CheckCircle className="h-3.5 w-3.5" /> Match
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Create mode */}
          {mode === "create" && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">

              {/* ── Section 1: Item catalogue fields ── */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <FlaskConical className="h-3 w-3 text-purple-500" /> Item details
                </p>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <div className="col-span-2 md:col-span-4">
                    <label className="block text-xs text-gray-400 mb-0.5">
                      Description * <span className="text-gray-300 font-normal">(invoice line pre-filled — edit to a generic/internal name if needed)</span>
                    </label>
                    <input value={newItem.description} onChange={function(e) { setNF("description", e.target.value); }}
                      className={inp} placeholder="Generic / internal product name" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Type</label>
                    <select value={newItem.item_type} onChange={function(e) { setNF("item_type", e.target.value); }} className={inp}>
                      <option value="">— select —</option>
                      {INV_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Generic Name</label>
                    <input value={newItem.generic_name} onChange={function(e) { setNF("generic_name", e.target.value); }}
                      className={inp} placeholder="e.g. amoxicillin" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Nominal Code</label>
                    <input value={newItem.nominal_code} onChange={function(e) { setNF("nominal_code", e.target.value); }}
                      className={inp} placeholder="e.g. 5000" />
                  </div>
                  <UnitSetupP item={newItem} setField={setNF} inp={inp} />
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Qty in Stock</label>
                    <input value={newItem.qty_in_stock} onChange={function(e) { setNF("qty_in_stock", e.target.value); }}
                      className={inp} type="number" min="0" placeholder="0" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Par Level</label>
                    <input value={newItem.par_level} onChange={function(e) { setNF("par_level", e.target.value); }}
                      className={inp} type="number" min="0" placeholder="0" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Reorder Point</label>
                    <input value={newItem.reorder_point} onChange={function(e) { setNF("reorder_point", e.target.value); }}
                      className={inp} type="number" min="0" placeholder="0" />
                  </div>
                </div>
              </div>

              {/* ── Section 2: Supplier line fields ── */}
              <div className="space-y-1.5 border-t border-gray-100 pt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <Truck className="h-3 w-3 text-blue-400" />
                  Supplier line
                  {supplierLinked
                    ? <span className="ml-1 font-normal normal-case text-green-600">({supplierLinked.name})</span>
                    : <span className="ml-1 font-normal normal-case text-amber-500">(no supplier linked yet — link a supplier above to save these details)</span>}
                </p>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Supplier Product Code</label>
                    <input value={supChild.supplier_code} onChange={function(e) { setSC("supplier_code", e.target.value); }}
                      className={inp} placeholder="Supplier's code" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Unit Price (from invoice)</label>
                    <input value={supChild.price} onChange={function(e) { setSC("price", e.target.value); }}
                      className={inp} placeholder="e.g. 45.00" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Outer Qty</label>
                    <input value={supChild.outer_quantity} onChange={function(e) { setSC("outer_quantity", e.target.value); }}
                      className={inp} placeholder="e.g. 100" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Pack Type</label>
                    <select value={supChild.outer_quantity_type || ""} onChange={function(e) { setSC("outer_quantity_type", e.target.value); }}
                      className={inp}>
                      <option value="">— type —</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Tub">Tub</option>
                      <option value="Packet">Packet</option>
                      <option value="Box">Box</option>
                      <option value="Bag">Bag</option>
                      <option value="Vial">Vial</option>
                      <option value="Ampoule">Ampoule</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Tube">Tube</option>
                      <option value="Canister">Canister</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Selling Units</label>
                    <input value={supChild.selling_units} onChange={function(e) { setSC("selling_units", e.target.value); }}
                      className={inp} placeholder="e.g. tablet" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Cost / Selling Unit</label>
                    <input value={supChild.cost_per_selling_unit} onChange={function(e) { setSC("cost_per_selling_unit", e.target.value); }}
                      className={inp} placeholder="auto-calc or enter" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Min Order Qty</label>
                    <input value={supChild.min_order_qty} onChange={function(e) { setSC("min_order_qty", e.target.value); }}
                      className={inp} placeholder="1" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Max Order Qty</label>
                    <input value={supChild.max_order_qty} onChange={function(e) { setSC("max_order_qty", e.target.value); }}
                      className={inp} placeholder="" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Notes</label>
                    <input value={supChild.notes} onChange={function(e) { setSC("notes", e.target.value); }}
                      className={inp} placeholder="" />
                  </div>
                </div>
              </div>

              {createErr && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{createErr}
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={handleCreate} disabled={creating}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg text-xs font-medium">
                  {creating ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  {creating ? "Creating…" : "Create & Link"}
                </button>
                <button onClick={function() { setMode("search"); }}
                  className="text-xs text-gray-500 hover:text-gray-700">Back to search</button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ── DN Line Match Panel ───────────────────────────────────────────────────────
// Shown below each invoice line when a PO is linked. Matches invoice line to a
// delivery note line by description/supplier code, so nominal codes & inventory
// item IDs flow through automatically.
const DNLineMatchPanel = ({ invoiceLine, poId, dnLines, dnMatch, onMatch }) => {
  // dnLines: flat array of { dn_id, dn_number, ...DNLine } fetched from linked DNs
  // dnMatch: null | { dn_line, dn_id, dn_number }

  var candidates = (dnLines || []).filter(function(dnl) {
    var descMatch = (dnl.inventory_item_description || "").toLowerCase()
      .includes((invoiceLine.description || "").toLowerCase().slice(0, 12));
    var codeMatch = invoiceLine.supplier_code && dnl.supplier_code &&
      dnl.supplier_code.toLowerCase() === invoiceLine.supplier_code.toLowerCase();
    return descMatch || codeMatch;
  });

  if (dnMatch) {
    return (
      <tr className="bg-green-50/40 border-b border-gray-100">
        <td colSpan={99} className="px-3 py-1">
          <span className="inline-flex items-center gap-1.5 text-xs text-green-700 font-medium">
            <CheckCircle className="h-3 w-3" />
            Matched to DN line: <strong>{dnMatch.dn_number}</strong> — {dnMatch.inventory_item_description}
            {dnMatch.nominal_code && <span className="ml-1 text-xs text-purple-700 font-mono">({dnMatch.nominal_code})</span>}
            <button onClick={function() { onMatch(null); }}
              className="ml-2 text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>
          </span>
        </td>
      </tr>
    );
  }

  if (!dnLines || dnLines.length === 0) return null;

  if (candidates.length === 0) {
    return (
      <tr className="border-b border-gray-100">
        <td colSpan={99} className="px-3 py-1">
          <span className="text-xs text-gray-400 italic">No matching DN line found for this invoice line.</span>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-gray-100 bg-blue-50/30">
      <td colSpan={99} className="px-3 py-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-blue-700 font-medium shrink-0">Match to DN line:</span>
          {candidates.map(function(dnl, ci) {
            return (
              <button key={ci}
                onClick={function() { onMatch(dnl); }}
                className="inline-flex items-center gap-1 text-xs bg-white border border-blue-200 hover:border-blue-400 hover:bg-blue-50 rounded px-2 py-0.5 text-blue-800">
                <Link2 className="h-3 w-3 shrink-0" />
                {dnl.dn_number} · {dnl.inventory_item_description}
                {dnl.nominal_code && <span className="ml-1 font-mono text-purple-600">({dnl.nominal_code})</span>}
              </button>
            );
          })}
        </div>
      </td>
    </tr>
  );
};

// ── PO Link Panel ─────────────────────────────────────────────────────────────
const POLinkPanel = ({ supplierName, supplierId, currentPoId, onLinked, onSkip }) => {
  const [pos, setPos] = useState(null); // null=loading, []=none found
  const [loading, setLoading] = useState(false);

  useEffect(function() {
    if (!supplierName && !supplierId) { setPos([]); return; }
    setLoading(true);
    var params = new URLSearchParams({ skip: 0, limit: 100 });
    // Fetch POs that are sent, approved, partial or received — i.e. goods could have arrived
    apiFetch("/api/inventory/purchase-orders?" + params.toString())
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var all = d.items || [];
        var relevant = all.filter(function(po) {
          if (!["sent", "approved", "partial", "received"].includes(po.status)) return false;
          if (supplierId && po.supplier_id && po.supplier_id === supplierId) return true;
          if (supplierName && po.supplier_name && po.supplier_name.toLowerCase().includes(supplierName.toLowerCase())) return true;
          return false;
        });
        setPos(relevant);
      })
      .catch(function() { setPos([]); })
      .finally(function() { setLoading(false); });
  }, [supplierId, supplierName]);

  if (loading) return (
    <div className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
      <RefreshCw className="h-3 w-3 animate-spin" /> Looking for matching purchase orders…
    </div>
  );

  if (!pos || pos.length === 0) return (
    <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm">
      <span className="text-gray-500 flex items-center gap-2">
        <ShoppingCart className="h-4 w-4 text-gray-400" />
        No matching sent/approved POs found for this supplier.
      </span>
      <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600 ml-4">Skip</button>
    </div>
  );

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
          <ShoppingCart className="h-3.5 w-3.5" /> Link to a Purchase Order
        </p>
        <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
      </div>
      <div className="space-y-1.5">
        {pos.map(function(po) {
          var isLinked = po.id === currentPoId;
          var meta = { sent: "Sent", approved: "Approved", partial: "Part Received", received: "Received" };
          return (
            <div key={po.id}
              className={"flex items-center justify-between rounded-lg px-3 py-2 border cursor-pointer " +
                (isLinked ? "bg-blue-100 border-blue-400" : "bg-white border-blue-200 hover:border-blue-400")}
              onClick={function() { onLinked(po); }}>
              <div>
                <span className="text-xs font-semibold text-gray-800">{po.po_number}</span>
                <span className="ml-2 text-xs text-gray-500">{po.supplier_name}</span>
                <span className={"ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium " +
                  (po.status === "received" ? "bg-green-100 text-green-700" :
                   po.status === "partial"  ? "bg-cyan-100 text-cyan-700" :
                   po.status === "sent"     ? "bg-purple-100 text-purple-700" :
                                             "bg-blue-100 text-blue-700")}>
                  {meta[po.status] || po.status}
                </span>
                <span className="ml-2 text-xs text-gray-400">
                  {(po.lines || []).length} line{(po.lines||[]).length !== 1 ? "s" : ""}
                  {po.created_at ? " · " + po.created_at.slice(0,10) : ""}
                </span>
              </div>
              {isLinked
                ? <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                : <span className="text-xs text-blue-600 font-medium shrink-0">Link</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Invoice Upload & Parse Component ──────────────────────────────────────────
const InvoiceUploadPanel = ({ onSaved, editInvoice, onCancelEdit }) => {
  const hubs = useHubs();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseSeconds, setParseSeconds] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [nominalSuggestions, setNominalSuggestions] = useState({});
  const [supplierLinked, setSupplierLinked] = useState(null);
  const [supplierSkipped, setSupplierSkipped] = useState(false);
  const [invoiceHub, setInvoiceHub] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [panelPos, setPanelPos] = useState({ x: 80, y: 80 });
  const [panelSize, setPanelSize] = useState({ w: 480, h: 640 });
  const [invMatches, setInvMatches] = useState({}); // idx -> { status, item, justCreated }
  const [poLinked, setPoLinked] = useState(null);   // PO linked to this invoice
  const [poSkipped, setPoSkipped] = useState(false);
  const [dnLines, setDnLines] = useState([]);       // flat DN lines from linked PO's DNs
  const [dnMatches, setDnMatches] = useState({});   // idx -> dnLine (matched DN line)
  const [priceUpdating, setPriceUpdating] = useState({}); // idx -> bool
  const [priceUpdated, setPriceUpdated] = useState({}); // idx -> bool
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const fileRef = useRef();
  const timerRef = useRef(null);

  // When opened in edit mode, pre-populate parsed state and fetch stored image
  useEffect(function() {
    if (!editInvoice) return;
    setParsed(Object.assign({}, editInvoice));
    setSupplierSkipped(true);
    setPoSkipped(true); // skip PO search in edit mode; link is shown separately
    setPreview(null);
    if (editInvoice.hub_id) {
      setInvoiceHub({ id: editInvoice.hub_id, name: editInvoice.hub_name });
    }
    apiFetch("/api/purchasing/invoices/" + editInvoice.id + "/image")
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (d && d.pages && d.pages.length) {
          setPreview(d.pages);
          setPreviewPage(0);
        }
      })
      .catch(function() {});
  }, [editInvoice]);

  // Fetch delivery note lines from DNs linked to this PO, and pull nominal codes from PO lines
  useEffect(function() {
    if (!poLinked || !poLinked.id) { setDnLines([]); return; }
    // Fetch DNs for this PO
    apiFetch("/api/inventory/delivery-notes?purchase_order_id=" + encodeURIComponent(poLinked.id) + "&limit=50")
      .then(function(r) { return r.ok ? r.json() : { items: [] }; })
      .then(function(d) {
        var flat = [];
        (d.items || []).forEach(function(dn) {
          (dn.lines || []).forEach(function(ln) {
            flat.push(Object.assign({}, ln, { dn_id: dn.id, dn_number: dn.dn_number }));
          });
        });
        setDnLines(flat);
      })
      .catch(function() { setDnLines([]); });
    // Pull nominal codes from PO lines into invoice line items
    setParsed(function(prev) {
      if (!prev || !prev.line_items) return prev;
      var poLines = poLinked.lines || [];
      var changed = false;
      var newItems = prev.line_items.map(function(li) {
        if (li.nominal_code) return li; // don't overwrite existing
        var match = poLines.find(function(pl) {
          if (li.supplier_code && pl.supplier_code &&
              pl.supplier_code.toLowerCase() === li.supplier_code.toLowerCase()) return true;
          if ((pl.inventory_item_description || "").toLowerCase()
              .includes((li.description || "").toLowerCase().slice(0, 12))) return true;
          return false;
        });
        if (match && match.nominal_code) {
          changed = true;
          return Object.assign({}, li, { nominal_code: match.nominal_code });
        }
        return li;
      });
      return changed ? Object.assign({}, prev, { line_items: newItems }) : prev;
    });
  }, [poLinked]);

  // Load nominal code suggestions when supplier is known
  useEffect(function() {
    if (!parsed || !parsed.supplier) return;
    apiFetch("/api/purchasing/invoices/suggest-nominal-codes?supplier=" + encodeURIComponent(parsed.supplier))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setNominalSuggestions(d.suggestions || {});
      })
      .catch(function() {});
  }, [parsed && parsed.supplier]);

  // Auto-apply nominal code suggestions to empty line items
  useEffect(function() {
    if (!parsed || Object.keys(nominalSuggestions).length === 0) return;
    setParsed(function(prev) {
      if (!prev) return prev;
      var changed = false;
      var newItems = prev.line_items.map(function(li) {
        if (li.nominal_code) return li;
        var key = (li.description || "").trim().toLowerCase();
        if (nominalSuggestions[key]) {
          changed = true;
          return Object.assign({}, li, { nominal_code: nominalSuggestions[key] });
        }
        return li;
      });
      return changed ? Object.assign({}, prev, { line_items: newItems }) : prev;
    });
  }, [nominalSuggestions]);

  const handleFileChange = function(f) {
    if (!f) return;
    setFile(f);
    setParsed(null);
    setError(null);
    setSuccess(null);
    setSupplierLinked(null);
    setSupplierSkipped(false);
    setInvoiceHub(null);
    if (f.type.startsWith("image/")) {
      var reader = new FileReader();
      reader.onload = function(e) { setPreview([e.target.result]); setPreviewPage(0); };
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
      setPreviewPage(0);
    }
  };

  const handleDrop = function(e) {
    e.preventDefault();
    var f = e.dataTransfer.files[0];
    if (f) handleFileChange(f);
  };

  const handleParse = async function() {
    if (!file) return;
    setParsing(true);
    setParseSeconds(0);
    setError(null);
    timerRef.current = setInterval(function() {
      setParseSeconds(function(s) { return s + 1; });
    }, 1000);
    try {
      var form = new FormData();
      form.append("file", file);
      var res = await apiFetch("/api/purchasing/invoices/parse", { method: "POST", body: form });
      var text = await res.text();
      var data;
      try { data = JSON.parse(text); } catch(_) {
        throw new Error("Server error: " + text.slice(0, 200));
      }
      if (!res.ok) throw new Error(data.detail || "Parse failed");
      setParsed(Object.assign({}, data, { line_items: data.line_items || [] }));
      if (data.image_data) {
        try { setPreview(JSON.parse(data.image_data)); } catch(_) { setPreview([data.image_data]); }
        setPreviewPage(0);
      }
      setSupplierLinked(null);
      setSupplierSkipped(false);
      setInvoiceHub(null);
      setInvMatches({});
    } catch(e) {
      setError(e.message);
    } finally {
      setParsing(false);
      clearInterval(timerRef.current);
    }
  };

  const updateField = function(field, value) {
    setParsed(function(prev) { return Object.assign({}, prev, { [field]: value }); });
  };

  const updateLineItem = function(idx, field, value) {
    setParsed(function(prev) {
      return Object.assign({}, prev, {
        line_items: prev.line_items.map(function(li, i) {
          return i === idx ? Object.assign({}, li, { [field]: value }) : li;
        })
      });
    });
  };

  const addLineItem = function() {
    setParsed(function(prev) {
      return Object.assign({}, prev, {
        line_items: (prev.line_items || []).concat([{
          description: "", quantity: "", unit_price: "", total: "",
          vat_rate: "", vat_amount: "", nominal_code: "",
          hub_id: (invoiceHub ? invoiceHub.id : ""),
          hub_name: (invoiceHub ? invoiceHub.name : "")
        }])
      });
    });
  };

  const removeLineItem = function(idx) {
    setParsed(function(prev) {
      return Object.assign({}, prev, {
        line_items: prev.line_items.filter(function(_, i) { return i !== idx; })
      });
    });
  };

  const applyHubToAll = function(hub) {
    setInvoiceHub(hub);
    setParsed(function(prev) {
      if (!prev) return prev;
      return Object.assign({}, prev, {
        hub_id: hub ? hub.id : "",
        hub_name: hub ? hub.name : "",
        line_items: prev.line_items.map(function(li) {
          return Object.assign({}, li, {
            hub_id: hub ? hub.id : "",
            hub_name: hub ? hub.name : ""
          });
        })
      });
    });
  };

  const handleHubSelected = function(hub) {
    applyHubToAll(hub);
  };

  const handleSave = async function() {
    if (!parsed) return;
    setSaving(true);
    setError(null);
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, 15000);
    var isEdit = !!(editInvoice && editInvoice.id);
    try {
      var payload = Object.assign({}, parsed, {
        supplier_id: supplierLinked ? supplierLinked.id : (parsed.supplier_id || ""),
        hub_id: invoiceHub ? invoiceHub.id : (parsed.hub_id || ""),
        hub_name: invoiceHub ? invoiceHub.name : (parsed.hub_name || ""),
        image_data: parsed.image_data || (preview ? JSON.stringify(preview) : ""),
        purchase_order_id: poLinked ? poLinked.id : (parsed.purchase_order_id || ""),
        purchase_order_number: poLinked ? poLinked.po_number : (parsed.purchase_order_number || ""),
        delivery_note_id: (function() {
          // Use the DN from the first matched line as the primary DN
          var first = Object.values(dnMatches).find(Boolean);
          return first ? (first.dn_id || "") : (parsed.delivery_note_id || "");
        })(),
        delivery_note_number: (function() {
          var first = Object.values(dnMatches).find(Boolean);
          return first ? (first.dn_number || "") : (parsed.delivery_note_number || "");
        })()
      });
      if (isEdit) {
        payload.editor_name = (user && user.name) || (user && user.email) || "Unknown";
        payload.editor_email = (user && user.email) || "";
      }
      var url = isEdit
        ? "/api/purchasing/invoices/" + editInvoice.id
        : "/api/purchasing/invoices";
      var method = isEdit ? "PATCH" : "POST";
      var res = await apiFetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      var text = await res.text();
      var data;
      try { data = JSON.parse(text); } catch(_) {
        throw new Error("Server returned non-JSON: " + text.slice(0, 200));
      }
      if (!res.ok) throw new Error((data && data.detail) ? JSON.stringify(data.detail) : "Save failed (" + res.status + ")");
      setSuccess(isEdit ? "Invoice " + data.id + " updated." : "Invoice saved as " + data.id);
      setParsed(null);
      setFile(null);
      setPreview(null);
      setSupplierLinked(null);
      setInvoiceHub(null);
      setPoLinked(null);
      setPoSkipped(false);
      if (onSaved) onSaved(data);
    } catch(e) {
      setError(e.name === "AbortError" ? "Save timed out - check backend is running." : e.message);
    } finally {
      clearTimeout(timer);
      setSaving(false);
    }
  };

  const reset = function() {
    setParsed(null); setFile(null); setPreview(null);
    setSupplierLinked(null); setSupplierSkipped(false); setInvoiceHub(null);
    setPoLinked(null); setPoSkipped(false);
    setError(null); setSuccess(null);
    setInvMatches({}); setDnLines([]); setDnMatches({});
    setPriceUpdating({}); setPriceUpdated({});
    if (onCancelEdit) onCancelEdit();
  };

  var headerFields = [
    { label: "Supplier", field: "supplier" },
    { label: "Invoice No.", field: "invoice_number" },
    { label: "Invoice Date", field: "invoice_date" },
    { label: "Due Date", field: "due_date" },
    { label: "Currency", field: "currency" },
    { label: "Subtotal", field: "subtotal" },
    { label: "VAT Total", field: "vat_total" },
    { label: "Total", field: "total" },
  ];

  var lineItemFields = [
    { key: "description", label: "Description", wide: true },
    { key: "supplier_code", label: "Supplier Code" },
    { key: "quantity", label: "Qty" },
    { key: "unit_price", label: "Unit Price" },
    { key: "total", label: "Total" },
    { key: "vat_rate", label: "VAT %" },
    { key: "vat_amount", label: "VAT Amt" },
    { key: "nominal_code", label: "Nominal Code" },
  ];

  return (
    <div className="space-y-6">
      {!parsed && (
        <div
          onDrop={handleDrop}
          onDragOver={function(e) { e.preventDefault(); }}
          onClick={function() { fileRef.current && fileRef.current.click(); }}
          className="border-2 border-dashed border-purple-300 rounded-xl p-10 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors"
        >
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
            onChange={function(e) { handleFileChange(e.target.files[0]); }} />
          <Upload className="h-10 w-10 text-purple-400 mx-auto mb-3" />
          <p className="text-base font-medium text-gray-700">Drop invoice here or click to browse</p>
          <p className="text-sm text-gray-400 mt-1">PDF, PNG, JPG or WebP</p>
          {file && (
            <p className="mt-3 text-sm font-medium text-purple-700 bg-purple-100 inline-block px-3 py-1 rounded-full">{file.name}</p>
          )}
        </div>
      )}

      {preview && preview.length > 0 && !parsed && (
        <div className="bg-gray-100 rounded-xl overflow-hidden max-h-64 flex items-center justify-center">
          <img src={preview[0]} alt="Invoice preview" className="max-h-64 object-contain" />
        </div>
      )}

      {file && !parsed && (
        <button onClick={handleParse} disabled={parsing}
          className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-xl font-medium">
          {parsing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          <span>{parsing ? ("Sending to AI... (" + parseSeconds + "s) - this takes 20-40s") : "Parse Invoice with AI"}</span>
        </button>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-3 text-sm">
          <CheckCircle className="h-4 w-4 shrink-0" /><span>{success}</span>
        </div>
      )}

      {parsed && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-purple-600" />
              {editInvoice ? "Edit Invoice " + (editInvoice.id || "") : "Parsed Invoice - Review and Edit"}
            </h3>
            <div className="flex items-center gap-3">
              {preview && (
                <button onClick={function() { setShowPreview(true); }}
                  className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 hover:bg-purple-50 rounded-lg px-2.5 py-1">
                  <Eye className="h-3.5 w-3.5" /> Preview Document
                </button>
              )}
              <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                <X className="h-3 w-3" /> Reset
              </button>
            </div>
          </div>

          {/* Floating draggable/resizable document preview panel */}
          {showPreview && preview && preview.length > 0 && (
            <div
              style={{
                position: "fixed",
                left: panelPos.x,
                top: panelPos.y,
                width: panelSize.w,
                height: panelSize.h,
                zIndex: 50,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
                borderRadius: "12px",
                overflow: "hidden",
                background: "#fff",
                border: "1px solid #e5e7eb",
                minWidth: 280,
                minHeight: 220
              }}
            >
              {/* Title bar – drag handle */}
              <div
                style={{ cursor: "grab", userSelect: "none" }}
                className="flex items-center justify-between bg-purple-700 px-3 py-2 shrink-0"
                onMouseDown={function(e) {
                  e.preventDefault();
                  var startX = e.clientX - panelPos.x;
                  var startY = e.clientY - panelPos.y;
                  var onMove = function(me) {
                    setPanelPos({ x: me.clientX - startX, y: me.clientY - startY });
                  };
                  var onUp = function() {
                    window.removeEventListener("mousemove", onMove);
                    window.removeEventListener("mouseup", onUp);
                  };
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-white text-xs font-semibold select-none">
                    Invoice Document {preview.length > 1 ? "— Page " + (previewPage + 1) + " of " + preview.length : ""}
                  </span>
                  {preview.length > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        disabled={previewPage === 0}
                        onClick={function() { setPreviewPage(function(p) { return Math.max(0, p - 1); }); }}
                        className="px-2 py-0.5 bg-white/20 hover:bg-white/30 disabled:opacity-30 text-white rounded text-xs">
                        &#8249;
                      </button>
                      <button
                        disabled={previewPage >= preview.length - 1}
                        onClick={function() { setPreviewPage(function(p) { return Math.min(preview.length - 1, p + 1); }); }}
                        className="px-2 py-0.5 bg-white/20 hover:bg-white/30 disabled:opacity-30 text-white rounded text-xs">
                        &#8250;
                      </button>
                    </div>
                  )}
                </div>
                <button onClick={function() { setShowPreview(false); }}
                  className="text-white/80 hover:text-white ml-2">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Image scrollable area */}
              <div className="flex-1 overflow-auto bg-gray-100 p-2">
                <img src={preview[previewPage]} alt={"Page " + (previewPage + 1)} className="w-full object-contain" />
              </div>

              {/* Resize handle – bottom-right corner */}
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 18,
                  height: 18,
                  cursor: "nwse-resize",
                  zIndex: 10
                }}
                onMouseDown={function(e) {
                  e.preventDefault();
                  var startX = e.clientX;
                  var startY = e.clientY;
                  var startW = panelSize.w;
                  var startH = panelSize.h;
                  var onMove = function(me) {
                    setPanelSize({
                      w: Math.max(280, startW + (me.clientX - startX)),
                      h: Math.max(220, startH + (me.clientY - startY))
                    });
                  };
                  var onUp = function() {
                    window.removeEventListener("mousemove", onMove);
                    window.removeEventListener("mouseup", onUp);
                  };
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" className="text-gray-400">
                  <line x1="6" y1="18" x2="18" y2="6" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="11" y1="18" x2="18" y2="11" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="16" y1="18" x2="18" y2="16" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
          )}

          {/* Supplier linking */}
          {!supplierLinked && !supplierSkipped && parsed.supplier && (
            <SupplierMatchPanel
              supplierName={parsed.supplier}
              supplierAddress={parsed.supplier_address || ""}
              onLinked={function(sup) { setSupplierLinked(sup); }}
              onSkip={function() { setSupplierSkipped(true); }}
            />
          )}
          {supplierLinked && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
              <span className="text-green-800">Linked to supplier: <strong>{supplierLinked.name}</strong></span>
              <button onClick={function() { setSupplierLinked(null); setSupplierSkipped(false); }}
                className="ml-auto text-xs text-gray-400 hover:text-gray-600">Change</button>
            </div>
          )}

          {/* PO link panel — shown after supplier is resolved */}
          {(supplierLinked || supplierSkipped) && !poLinked && !poSkipped && (
            <POLinkPanel
              supplierName={supplierLinked ? supplierLinked.name : (parsed.supplier || "")}
              supplierId={supplierLinked ? supplierLinked.id : ""}
              currentPoId={null}
              onLinked={function(po) { setPoLinked(po); }}
              onSkip={function() { setPoSkipped(true); }}
            />
          )}
          {poLinked && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm">
              <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="text-blue-800">Linked to PO: <strong>{poLinked.po_number}</strong>
                <span className="ml-2 text-xs text-blue-600">{poLinked.supplier_name} · {poLinked.status}</span>
              </span>
              <button onClick={function() { setPoLinked(null); setPoSkipped(false); }}
                className="ml-auto text-xs text-gray-400 hover:text-gray-600">Change</button>
            </div>
          )}
          {poSkipped && !poLinked && (
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
              <ShoppingCart className="h-4 w-4 text-gray-400 shrink-0" />
              <span className="text-gray-500">No PO linked to this invoice.</span>
              <button onClick={function() { setPoSkipped(false); }}
                className="ml-auto text-xs text-purple-500 hover:text-purple-700">Link to PO</button>
            </div>
          )}

          {/* Purchaser address / hub match */}
          {parsed.purchaser_address && (
            <HubMatchBanner
              purchaserAddress={parsed.purchaser_address}
              onHubSelected={handleHubSelected}
            />
          )}

          {/* Nominal code suggestions info */}
          {Object.keys(nominalSuggestions).length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-2.5 text-xs text-purple-700 flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 shrink-0" />
              {Object.keys(nominalSuggestions).length} nominal code(s) pre-filled from previous invoices for this supplier.
            </div>
          )}

          {/* Header fields */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 rounded-xl p-4 border border-gray-200">
            {headerFields.map(function(item) {
              return (
                <div key={item.field}>
                  <label className="block text-xs text-gray-500 mb-0.5">{item.label}</label>
                  <input value={parsed[item.field] || ""} onChange={function(e) { updateField(item.field, e.target.value); }}
                    className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-1 focus:ring-purple-400" />
                </div>
              );
            })}
            <div className="col-span-2 md:col-span-4">
              <label className="block text-xs text-gray-500 mb-0.5">Purchaser Address</label>
              <input value={parsed.purchaser_address || ""} onChange={function(e) { updateField("purchaser_address", e.target.value); }}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-1 focus:ring-purple-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-0.5">Hub</label>
              <select
                value={parsed.hub_id || ""}
                onChange={function(e) {
                  var selected = hubs.find(function(h) { return h.id === e.target.value; }) || null;
                  applyHubToAll(selected);
                }}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-1 focus:ring-purple-400 bg-white">
                <option value="">-- Select hub --</option>
                {hubs.map(function(h) {
                  return <option key={h.id} value={h.id}>{h.name}</option>;
                })}
              </select>
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="block text-xs text-gray-500 mb-0.5">Notes / Payment Terms</label>
              <input value={parsed.notes || ""} onChange={function(e) { updateField("notes", e.target.value); }}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-1 focus:ring-purple-400" />
            </div>
          </div>

          {/* Line items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Line Items ({parsed.line_items.length})
              </span>
              <button onClick={addLineItem} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add line
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {lineItemFields.map(function(f) {
                      return (
                        <th key={f.key} className={"px-3 py-2 text-left text-xs text-gray-500 font-medium " + (f.wide ? "min-w-48" : "w-24")}>
                          {f.label}
                        </th>
                      );
                    })}
                    <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium w-32">Hub</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.line_items.map(function(li, idx) {
                    return (
                      <React.Fragment key={idx}>
                        <tr className="hover:bg-gray-50 border-b border-gray-100">
                          {lineItemFields.map(function(f) {
                            var isSuggested = f.key === "nominal_code" && nominalSuggestions[(li.description || "").trim().toLowerCase()] && !li.nominal_code_manually_set;
                            return (
                              <td key={f.key} className="px-2 py-1">
                                <input
                                  value={li[f.key] || ""}
                                  onChange={function(e) { updateLineItem(idx, f.key, e.target.value); }}
                                  className={"w-full text-xs border rounded px-1.5 py-1 focus:ring-1 focus:ring-purple-300 " + (isSuggested ? "border-purple-300 bg-purple-50" : "border-gray-200")}
                                />
                              </td>
                            );
                          })}
                          <td className="px-2 py-1 w-32">
                            <select
                              value={li.hub_id || ""}
                              onChange={function(e) {
                                var selected = hubs.find(function(h) { return h.id === e.target.value; });
                                updateLineItem(idx, "hub_id", e.target.value);
                                updateLineItem(idx, "hub_name", selected ? selected.name : "");
                              }}
                              className="w-full text-xs border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-purple-300 bg-white">
                              <option value="">-- Hub --</option>
                              {hubs.map(function(h) {
                                return <option key={h.id} value={h.id}>{h.name}</option>;
                              })}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <button onClick={function() {
                              removeLineItem(idx);
                              setInvMatches(function(prev) {
                                var next = Object.assign({}, prev);
                                delete next[idx];
                                return next;
                              });
                              setDnMatches(function(prev) {
                                var next = Object.assign({}, prev);
                                delete next[idx];
                                return next;
                              });
                            }} className="text-red-400 hover:text-red-600">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                        {poLinked && (
                          <DNLineMatchPanel
                            invoiceLine={li}
                            poId={poLinked.id}
                            dnLines={dnLines}
                            dnMatch={dnMatches[idx] || null}
                            onMatch={function(dnl) {
                              setDnMatches(function(prev) { return Object.assign({}, prev, { [idx]: dnl }); });
                              if (dnl && dnl.nominal_code && !li.nominal_code) {
                                updateLineItem(idx, "nominal_code", dnl.nominal_code);
                              }
                              // Auto-populate invMatches from DN line if it carries an inventory item
                              if (dnl && dnl.inventory_item_id) {
                                setInvMatches(function(prev) {
                                  if (prev[idx] && prev[idx].status === "matched") return prev; // don't overwrite existing
                                  return Object.assign({}, prev, { [idx]: {
                                    status: "matched",
                                    item: { id: dnl.inventory_item_id, description: dnl.inventory_item_description },
                                    autoMatched: true
                                  }});
                                });
                              }
                            }}
                          />
                        )}
                        <InvMatchRow
                          key={"inv-" + idx}
                          lineItem={li}
                          supplierLinked={supplierLinked || (parsed.supplier_id || parsed.supplier ? { id: parsed.supplier_id || "", name: parsed.supplier || "" } : null)}
                          match={invMatches[idx] || null}
                          onMatch={function(m) {
                            setInvMatches(function(prev) {
                              return Object.assign({}, prev, { [idx]: m });
                            });
                          }}
                        />
                        {/* Price update row — shown when DN matched and unit_price on invoice differs from stored price */}
                        {(function() {
                          var dnm = dnMatches[idx];
                          if (!dnm || !dnm.inventory_item_id) return null;
                          if (!li.unit_price) return null;
                          return (
                            <tr className="border-b border-gray-100 bg-amber-50/30">
                              <td colSpan={99} className="px-3 py-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-amber-800">
                                    Update supplier price for <strong>{dnm.inventory_item_description}</strong> to <strong>£{li.unit_price}</strong>?
                                  </span>
                                  {priceUpdated[idx] ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                                      <CheckCircle className="h-3 w-3" /> Price updated
                                    </span>
                                  ) : (
                                    <button
                                      disabled={!!priceUpdating[idx]}
                                      onClick={async function() {
                                        setPriceUpdating(function(p) { return Object.assign({}, p, { [idx]: true }); });
                                        try {
                                          await apiFetch("/api/purchasing/invoices/price-update", {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                              inventory_item_id: dnm.inventory_item_id,
                                              supplier_child_id: dnm.supplier_child_id || "",
                                              new_price: li.unit_price
                                            })
                                          });
                                          setPriceUpdated(function(p) { return Object.assign({}, p, { [idx]: true }); });
                                        } catch(_) {}
                                        setPriceUpdating(function(p) { var n = Object.assign({}, p); delete n[idx]; return n; });
                                      }}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-60">
                                      {priceUpdating[idx] ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                      Update Price
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                  {parsed.line_items.length === 0 && (
                    <tr><td colSpan={lineItemFields.length + 1} className="px-4 py-4 text-xs text-gray-400 text-center">No line items - add manually</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment status */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Status</p>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!(parsed.paid)} onChange={function(e) { updateField("paid", e.target.checked); }}
                  className="w-4 h-4 accent-green-600" />
                <span className="text-sm font-medium text-gray-700">Marked as Paid</span>
              </label>
              {parsed.paid && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">Method:</label>
                    <select value={parsed.payment_method || ""} onChange={function(e) { updateField("payment_method", e.target.value); }}
                      className="text-sm border border-gray-200 rounded-lg px-2.5 py-1 focus:ring-1 focus:ring-purple-400 bg-white">
                      <option value="">-- Select --</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="debit_card">Debit Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="cash">Cash</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">Date Paid:</label>
                    <input type="date" value={parsed.paid_date || ""} onChange={function(e) { updateField("paid_date", e.target.value); }}
                      className="text-sm border border-gray-200 rounded-lg px-2.5 py-1 focus:ring-1 focus:ring-purple-400" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Audit trail (edit mode only) */}
          {editInvoice && (parsed.audit_log || []).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Edit History</p>
              <div className="space-y-1">
                {(parsed.audit_log || []).slice().reverse().map(function(entry, i) {
                  return (
                    <div key={i} className="text-xs text-amber-800 flex gap-2">
                      <span className="text-amber-400">{entry.at ? entry.at.replace("T", " ").slice(0, 16) : ""}</span>
                      <span>Edited by <strong>{entry.by}</strong>{entry.email ? " (" + entry.email + ")" : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Line linking summary */}
          {parsed.line_items.length > 0 && (function() {
            var invLinked = Object.values(invMatches).filter(function(m) { return m && m.status === "matched"; }).length;
            var invSkipped = Object.values(invMatches).filter(function(m) { return m && m.status === "skip"; }).length;
            var invPending = parsed.line_items.length - invLinked - invSkipped;
            var dnMatched = poLinked ? Object.values(dnMatches).filter(Boolean).length : null;
            var allInvDone = invPending === 0;
            return (
              <div className="space-y-2">
                {/* Inventory stock linking */}
                <div className={"flex items-center gap-3 rounded-xl px-4 py-3 text-sm border " + (allInvDone ? "bg-green-50 border-green-200 text-green-800" : "bg-blue-50 border-blue-200 text-blue-800")}>
                  <PackagePlus className="h-4 w-4 shrink-0" />
                  <span className="font-medium">Stock linking:</span>
                  <span className="text-xs">
                    {invLinked} linked·{invSkipped} not stocked·{invPending} pending
                    {invPending > 0 && <span className="ml-1">— use “Link or add to inventory” on each unlinked line</span>}
                  </span>
                </div>
                {/* DN matching (only when PO linked) */}
                {poLinked && (function() {
                  var pending = parsed.line_items.length - dnMatched;
                  return (
                    <div className={"flex items-center gap-3 rounded-xl px-4 py-3 text-sm border " + (pending > 0 ? "bg-cyan-50 border-cyan-200 text-cyan-800" : "bg-green-50 border-green-200 text-green-800")}>
                      <Truck className="h-4 w-4 shrink-0" />
                      <span className="font-medium">Delivery note matching:</span>
                      <span className="text-xs">
                        {dnMatched} of {parsed.line_items.length} lines matched to a delivery note
                        {pending > 0 && dnLines.length > 0 && <span className="ml-1">— click a DN suggestion below each line</span>}
                        {dnLines.length === 0 && <span className="ml-1 text-amber-700">— no delivery notes found for this PO yet</span>}
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-xl font-medium text-sm">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span>{saving ? "Saving..." : (editInvoice ? "Save Changes" : "Save Invoice to Database")}</span>
            </button>
            <button onClick={reset} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50">{editInvoice ? "Cancel Edit" : "Cancel"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ?? Stored Invoices List ??????????????????????????????????????????????????????
const InvoicesList = ({ refresh, onEditInvoice, xeroStatus, onXeroPost }) => {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [xeroPosting, setXeroPosting] = useState({}); // id -> bool
  const [xeroResults, setXeroResults] = useState({});  // id -> { xero_invoice_number, error }
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterPaid, setFilterPaid] = useState("");
  const [filterHub, setFilterHub] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async function() {
    setLoading(true);
    setLoadError(null);
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, 10000);
    try {
      var params = new URLSearchParams({ skip: 0, limit: 100 });
      if (search) params.set("search", search);
      var res = await apiFetch("/api/purchasing/invoices?" + params.toString(), { signal: controller.signal });
      var data = await res.json();
      setInvoices(data.items || []);
      setTotal(data.total || 0);
    } catch(err) {
      if (err.name === "AbortError") {
        setLoadError("Request timed out - check backend is running.");
      } else {
        setLoadError(err.message);
      }
      console.error("Failed to load invoices:", err);
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [search]);

  useEffect(function() { load(); }, [load, refresh]);

  const handleDelete = async function(id) {
    if (!window.confirm("Delete this invoice?")) return;
    await apiFetch("/api/purchasing/invoices/" + id, { method: "DELETE" });
    load();
  };

  const postToXero = async function(inv) {
    setXeroPosting(function(p) { return Object.assign({}, p, { [inv.id]: true }); });
    try {
      var res = await apiFetch("/api/xero/invoices/" + inv.id, { method: "POST" });
      var data = await res.json();
      if (!res.ok) {
        setXeroResults(function(p) { return Object.assign({}, p, { [inv.id]: { error: data.detail || "Xero error" } }); });
      } else {
        setXeroResults(function(p) { return Object.assign({}, p, { [inv.id]: data }); });
        if (onXeroPost) onXeroPost();
        load(); // refresh to show xero_invoice_number stored on the doc
      }
    } catch(err) {
      setXeroResults(function(p) { return Object.assign({}, p, { [inv.id]: { error: err.message } }); });
    } finally {
      setXeroPosting(function(p) { var n = Object.assign({}, p); delete n[inv.id]; return n; });
    }
  };

  const toggleExpanded = function(id) { setExpanded(expanded === id ? null : id); };

  var buildExportRows = function(invList) {
    var rows = [];
    invList.forEach(function(inv) {
      var base = {
        "Invoice ID": inv.id || "",
        "Invoice No.": inv.invoice_number || "",
        "Invoice Date": inv.invoice_date || "",
        "Due Date": inv.due_date || "",
        "Supplier": inv.supplier || "",
        "Supplier Address": inv.supplier_address || "",
        "Purchaser Address": inv.purchaser_address || "",
        "Hub": inv.hub_name || "",
        "Currency": inv.currency || "GBP",
        "Subtotal": inv.subtotal || "",
        "VAT Total": inv.vat_total || "",
        "Total": inv.total || "",
        "Paid": inv.paid ? "Yes" : "No",
        "Payment Method": (inv.payment_method || "").replace("_", " "),
        "Date Paid": inv.paid_date || "",
        "Notes": inv.notes || "",
        "File": inv.file_name || "",
        "Created At": inv.created_at ? inv.created_at.replace("T", " ").slice(0, 16) : "",
        "Line Description": "",
        "Supplier Code": "",
        "Qty": "",
        "Unit Price": "",
        "Line Total": "",
        "VAT Rate": "",
        "VAT Amount": "",
        "Nominal Code": "",
        "Line Hub": ""
      };
      if (!inv.line_items || inv.line_items.length === 0) {
        rows.push(base);
      } else {
        inv.line_items.forEach(function(li, idx) {
          rows.push(Object.assign({}, base, {
            "Line Description": li.description || "",
            "Supplier Code": li.supplier_code || "",
            "Qty": li.quantity || "",
            "Unit Price": li.unit_price || "",
            "Line Total": li.total || "",
            "VAT Rate": li.vat_rate || "",
            "VAT Amount": li.vat_amount || "",
            "Nominal Code": li.nominal_code || "",
            "Line Hub": li.hub_name || ""
          }));
          if (idx > 0) {
            var k;
            for (k in base) { rows[rows.length - 1][k] = rows[rows.length - 1][k]; }
            ["Invoice ID","Invoice No.","Invoice Date","Due Date","Supplier","Supplier Address",
             "Purchaser Address","Hub","Currency","Subtotal","VAT Total","Total","Paid",
             "Payment Method","Date Paid","Notes","File","Created At"].forEach(function(col) {
              rows[rows.length - 1][col] = "";
            });
          }
        });
      }
    });
    return rows;
  };

  var exportCSV = function() {
    var rows = buildExportRows(displayed);
    var ws = XLSX.utils.json_to_sheet(rows);
    var csv = XLSX.utils.sheet_to_csv(ws);
    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = "invoices_" + new Date().toISOString().slice(0, 10) + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  var exportExcel = function() {
    var rows = buildExportRows(displayed);
    var ws = XLSX.utils.json_to_sheet(rows);
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, "invoices_" + new Date().toISOString().slice(0, 10) + ".xlsx");
  };

  var hubOptions = invoices.reduce(function(acc, inv) {
    if (inv.hub_id && inv.hub_name && !acc.find(function(h) { return h.id === inv.hub_id; })) {
      acc.push({ id: inv.hub_id, name: inv.hub_name });
    }
    return acc;
  }, []).sort(function(a, b) { return a.name.localeCompare(b.name); });

  var displayed = invoices.filter(function(inv) {
    if (filterPaid === "paid" && !inv.paid) return false;
    if (filterPaid === "unpaid" && inv.paid) return false;
    if (filterHub && inv.hub_id !== filterHub) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Search invoices..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
        </div>
        <select value={filterPaid} onChange={function(e) { setFilterPaid(e.target.value); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white">
          <option value="">All payment status</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        {hubOptions.length > 0 && (
          <select value={filterHub} onChange={function(e) { setFilterHub(e.target.value); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white">
            <option value="">All hubs</option>
            {hubOptions.map(function(h) {
              return <option key={h.id} value={h.id}>{h.name}</option>;
            })}
          </select>
        )}
        <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" title="Refresh">
          <RefreshCw className={"h-4 w-4" + (loading ? " animate-spin" : "")} />
        </button>
        <span className="text-sm text-gray-500">
          {displayed.length}{displayed.length !== total ? " of " + total : ""} invoice{total !== 1 ? "s" : ""}
        </span>
        {displayed.length > 0 && (
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700">
              <Download className="h-4 w-4" /> CSV
            </button>
            <button onClick={exportExcel}
              className="flex items-center gap-1.5 px-3 py-2 border border-green-300 rounded-lg hover:bg-green-50 text-sm text-green-700">
              <Download className="h-4 w-4" /> Excel
            </button>
          </div>
        )}
      </div>

      {loadError && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" /><span>{loadError}</span>
        </div>
      )}

      {displayed.length === 0 && !loading && !loadError && (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p>{invoices.length === 0 ? "No invoices stored yet. Upload one above." : "No invoices match the current filters."}</p>
        </div>
      )}

      <div className="space-y-3">
        {displayed.map(function(inv) {
          var isExpanded = expanded === inv.id;
          return (
            <div key={inv.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={function() { toggleExpanded(inv.id); }}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                    <Receipt className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{inv.supplier || "No supplier"}</p>
                    <p className="text-xs text-gray-500">
                      {inv.id} - {inv.invoice_number || "no ref"} - {inv.invoice_date || "no date"}
                      {inv.hub_name ? " - Hub: " + inv.hub_name : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-bold text-gray-900">{inv.currency || ""}{inv.total || "0.00"}</p>
                    <p className="text-xs text-gray-400">{(inv.line_items || []).length} lines</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.purchase_order_number && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        <ShoppingCart className="h-3 w-3" />
                        {inv.purchase_order_number}
                      </span>
                    )}
                    {inv.paid && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        <CheckCircle className="h-3 w-3" />
                        {inv.payment_method ? inv.payment_method.replace("_", " ") : "Paid"}
                      </span>
                    )}
                    {/* Xero post button */}
                    {xeroStatus && xeroStatus.connected && (function() {
                      var result = xeroResults[inv.id];
                      var isPosting = !!xeroPosting[inv.id];
                      var alreadyPosted = inv.xero_invoice_id || (result && result.xero_invoice_number);
                      if (alreadyPosted) {
                        return (
                          <span onClick={function(e){e.stopPropagation();}} className="hidden sm:inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium" title={"Xero: " + (inv.xero_invoice_number || result.xero_invoice_number)}>
                            <Zap className="h-3 w-3" />
                            {inv.xero_invoice_number || result.xero_invoice_number || "In Xero"}
                          </span>
                        );
                      }
                      if (result && result.error) {
                        return (
                          <button onClick={function(e) { e.stopPropagation(); postToXero(inv); }}
                            className="hidden sm:inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full" title={result.error}>
                            <AlertCircle className="h-3 w-3" /> Retry Xero
                          </button>
                        );
                      }
                      return (
                        <button onClick={function(e) { e.stopPropagation(); postToXero(inv); }}
                          disabled={isPosting}
                          className="hidden sm:inline-flex items-center gap-1 text-xs bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full disabled:opacity-50">
                          {isPosting ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                          {isPosting ? "Posting…" : "Post to Xero"}
                        </button>
                      );
                    })()}
                    <button onClick={function(e) { e.stopPropagation(); if (onEditInvoice) onEditInvoice(inv); }}
                      className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="Edit invoice">
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={function(e) { e.stopPropagation(); handleDelete(inv.id); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 px-5 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-3 text-xs text-gray-600 border-b border-gray-100 mb-3">
                    <div><span className="text-gray-400">Subtotal: </span>{inv.subtotal || "0.00"}</div>
                    <div><span className="text-gray-400">VAT: </span>{inv.vat_total || "0.00"}</div>
                    <div><span className="text-gray-400">Due: </span>{inv.due_date || "n/a"}</div>
                    <div><span className="text-gray-400">File: </span>{inv.file_name || "n/a"}</div>
                    {inv.hub_name && <div><span className="text-gray-400">Hub: </span>{inv.hub_name}</div>}
                    {inv.supplier_address && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-gray-400">Supplier Address: </span>{inv.supplier_address}
                      </div>
                    )}
                    {inv.purchaser_address && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-gray-400">Purchaser Address: </span>{inv.purchaser_address}
                      </div>
                    )}
                    <div>
                      <span className="text-gray-400">Payment: </span>
                      {inv.paid
                        ? <span className="text-green-700 font-medium">
                            {inv.payment_method ? inv.payment_method.replace("_", " ") : "Paid"}
                            {inv.paid_date ? " on " + inv.paid_date : ""}
                          </span>
                        : <span className="text-orange-600">Unpaid</span>}
                    </div>
                    {inv.purchase_order_number && (
                      <div className="col-span-2 md:col-span-4 flex items-center gap-1">
                        <ShoppingCart className="h-3 w-3 text-blue-400 shrink-0" />
                        <span className="text-gray-400">Linked PO: </span>
                        <span className="text-blue-700 font-medium">{inv.purchase_order_number}</span>
                      </div>
                    )}
                    {inv.delivery_note_number && (
                      <div className="col-span-2 md:col-span-4 flex items-center gap-1">
                        <Truck className="h-3 w-3 text-cyan-500 shrink-0" />
                        <span className="text-gray-400">Linked DN: </span>
                        <span className="text-cyan-700 font-medium">{inv.delivery_note_number}</span>
                      </div>
                    )}
                    {inv.notes && <div className="col-span-2 md:col-span-4"><span className="text-gray-400">Notes: </span>{inv.notes}</div>}
                    {(inv.audit_log || []).length > 0 && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-gray-400">Last edited: </span>
                        {(function() { var last = inv.audit_log[inv.audit_log.length - 1]; return last.at.replace("T"," ").slice(0,16) + " by " + last.by; })()}
                      </div>
                    )}
                  </div>
                  {(inv.line_items || []).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400 border-b border-gray-100">
                            <th className="py-1.5 text-left font-medium">Description</th>
                            <th className="py-1.5 text-left font-medium w-24">Supplier Code</th>
                            <th className="py-1.5 text-right font-medium w-12">Qty</th>
                            <th className="py-1.5 text-right font-medium w-20">Unit</th>
                            <th className="py-1.5 text-right font-medium w-20">Total</th>
                            <th className="py-1.5 text-right font-medium w-16">VAT%</th>
                            <th className="py-1.5 text-right font-medium w-20">VAT Amt</th>
                            <th className="py-1.5 text-left font-medium w-28 pl-3">Nominal Code</th>
                            <th className="py-1.5 text-left font-medium w-24 pl-3">Hub</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {inv.line_items.map(function(li, i) {
                            return (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="py-1.5 pr-3 text-gray-800">{li.description || "n/a"}</td>
                                <td className="py-1.5 text-gray-500 font-mono text-xs">{li.supplier_code || ""}</td>
                                <td className="py-1.5 text-right text-gray-600">{li.quantity}</td>
                                <td className="py-1.5 text-right text-gray-600">{li.unit_price}</td>
                                <td className="py-1.5 text-right font-medium text-gray-800">{li.total}</td>
                                <td className="py-1.5 text-right text-gray-500">{li.vat_rate}</td>
                                <td className="py-1.5 text-right text-gray-500">{li.vat_amount}</td>
                                <td className="py-1.5 pl-3 text-purple-700 font-medium">{li.nominal_code}</td>
                                <td className="py-1.5 pl-3 text-gray-500">{li.hub_name}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No line items recorded.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Supplier Form Modal ─────────────────────────────────────────────────────

const SupplierFormModal = ({ initial, onClose, onSaved }) => {
  var isEdit = !!(initial && initial.id);
  var blank = { name: "", category: "Active", postcode: "", address: "", email: "", phone: "", website: "", notes: "", aliases: "" };
  var [form, setForm] = useState(isEdit ? {
    name: initial.name || "",
    category: initial.category || "",
    postcode: initial.postcode || "",
    address: initial.address || "",
    email: initial.email || "",
    phone: initial.phone || "",
    website: initial.website || "",
    notes: initial.notes || "",
    aliases: (initial.aliases || []).join("\n")
  } : blank);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);

  var inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none";
  var setField = function(k, v) { setForm(function(p) { return Object.assign({}, p, { [k]: v }); }); };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      var url = isEdit
        ? "/api/purchasing/suppliers/" + initial.id
        : "/api/purchasing/suppliers";
      var payload = Object.assign({}, form, {
        aliases: (form.aliases || "")
          .split(/\r?\n/)
          .map(function(s) { return s.trim(); })
          .filter(function(s) { return s.length > 0; })
      });
      var res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      onSaved(data);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Building className="h-5 w-5 text-purple-600" />
            {isEdit ? "Edit Supplier" : "New Supplier"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-auto flex-1 px-6 py-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
              <input value={form.name} onChange={function(e) { setField("name", e.target.value); }}
                className={inp} placeholder="Supplier name" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category || ""} onChange={function(e) { setField("category", e.target.value); }} className={inp}>
                <option value="">— not set —</option>
                <option value="Active">Active</option>
                <option value="Non-inventory">Non-inventory</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Postcode</label>
                <input value={form.postcode} onChange={function(e) { setField("postcode", e.target.value); }}
                  className={inp} placeholder="e.g. SW1A 1AA" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input value={form.phone} onChange={function(e) { setField("phone", e.target.value); }}
                  className={inp} placeholder="+44..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
              <textarea value={form.address} onChange={function(e) { setField("address", e.target.value); }}
                className={inp} rows={3} placeholder="Full address" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input value={form.email} onChange={function(e) { setField("email", e.target.value); }}
                className={inp} type="email" placeholder="orders@supplier.com" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
              <input value={form.website} onChange={function(e) { setField("website", e.target.value); }}
                className={inp} placeholder="https://..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <textarea value={form.notes} onChange={function(e) { setField("notes", e.target.value); }}
                className={inp} rows={2} placeholder="Internal notes" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Aliases <span className="text-gray-400 font-normal">(one per line — alternate names from EzyVet, Xero, invoices, etc.)</span>
              </label>
              <textarea value={form.aliases} onChange={function(e) { setField("aliases", e.target.value); }}
                className={inp + " font-mono"} rows={4}
                placeholder={"MWI\nMWI Animal Health\nNVS"} />
              <p className="text-xs text-gray-500 mt-1">Used to match this supplier against inventory items imported with different name spellings.</p>
            </div>
          </div>

          {error && (
            <div className="mx-6 mb-2 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEdit ? "Save Changes" : "Create Supplier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Xero Supplier Sync Panel ────────────────────────────────────────────────

const XeroSupplierSyncPanel = ({ xeroStatus, onSyncComplete }) => {
  var [open, setOpen] = useState(false);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState(null);
  var [contacts, setContacts] = useState([]); // Xero contacts
  var [matched, setMatched] = useState({});   // { xero_contact_id: vetflow_supplier_id } auto-matched
  var [linked, setLinked] = useState({});     // { xero_contact_id: vetflow_supplier_id } already linked
  var [linkMap, setLinkMap] = useState({});   // user selections: { xero_contact_id: vf_id | "NEW" | "SKIP" }
  var [selected, setSelected] = useState({}); // { xero_contact_id: bool } — import selection
  var [importing, setImporting] = useState(false);
  var [importResult, setImportResult] = useState(null);

  // ── Export state
  var [vfSuppliers, setVfSuppliers] = useState([]);
  var [exportSelected, setExportSelected] = useState({});
  var [exporting, setExporting] = useState(false);
  var [exportResult, setExportResult] = useState(null);
  var [tab, setTab] = useState("import"); // "import" | "export"

  var fetchContacts = async function() {
    setLoading(true); setError(null); setImportResult(null);
    try {
      var res = await apiFetch("/api/xero/contacts");
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to fetch Xero contacts");
      setContacts(data.xero_contacts || []);
      setMatched(data.matched || {});
      setLinked(data.already_linked || {});
      // Pre-populate linkMap with auto-matches
      var init = {};
      Object.entries(data.matched || {}).forEach(function([cid, vid]) { init[cid] = vid; });
      Object.entries(data.already_linked || {}).forEach(function([cid, vid]) { init[cid] = vid; });
      setLinkMap(init);
      // Pre-select unlinked, auto-matched contacts for import
      var sel = {};
      (data.xero_contacts || []).forEach(function(c) {
        var cid = c.ContactID;
        if (!data.already_linked[cid]) sel[cid] = true;
      });
      setSelected(sel);
    } catch(err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  var fetchVfSuppliers = async function() {
    var res = await apiFetch("/api/purchasing/suppliers?limit=500");
    var data = await res.json();
    setVfSuppliers(data.items || []);
    var sel = {};
    (data.items || []).forEach(function(s) { sel[s.id] = false; });
    setExportSelected(sel);
  };

  var handleOpen = async function() {
    setOpen(true);
    await Promise.all([fetchContacts(), fetchVfSuppliers()]);
  };

  var handleImport = async function() {
    var contactIds = contacts
      .filter(function(c) { return selected[c.ContactID] && linkMap[c.ContactID] !== "SKIP"; })
      .map(function(c) { return c.ContactID; });
    if (contactIds.length === 0) { setError("Select at least one contact to import."); return; }
    setImporting(true); setError(null);
    try {
      var resolvedLinkMap = {};
      contactIds.forEach(function(cid) {
        var v = linkMap[cid];
        resolvedLinkMap[cid] = (v && v !== "NEW") ? v : null;
      });
      var res = await apiFetch("/api/xero/contacts/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contact_ids: contactIds, link_map: resolvedLinkMap })
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      setImportResult(data);
      if (onSyncComplete) onSyncComplete();
      await fetchContacts();
    } catch(err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  var handleExport = async function() {
    var supplierIds = Object.keys(exportSelected).filter(function(id) { return exportSelected[id]; });
    if (supplierIds.length === 0) { setError("Select at least one supplier to export."); return; }
    setExporting(true); setError(null); setExportResult(null);
    try {
      var res = await apiFetch("/api/xero/contacts/export", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supplier_ids: supplierIds })
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Export failed");
      setExportResult(data);
      if (onSyncComplete) onSyncComplete();
      await fetchVfSuppliers();
    } catch(err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (!xeroStatus || !xeroStatus.connected) {
    return (
      <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500">
        <Zap className="h-4 w-4 text-gray-300" />
        <span>Connect to Xero to sync suppliers</span>
        <a href={API + "/api/xero/connect"}
          className="ml-auto flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 font-medium">
          <ExternalLink className="h-3 w-3" /> Connect Xero
        </a>
      </div>
    );
  }

  return (
    <div className="border border-emerald-200 bg-emerald-50/30 rounded-xl">
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-6 h-6 bg-emerald-500 rounded flex items-center justify-center shrink-0">
          <Zap className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-800">Xero Supplier Sync</p>
          <p className="text-xs text-emerald-600">{xeroStatus.tenant_name}</p>
        </div>
        <button onClick={function() { if (!open) { handleOpen(); } else { setOpen(false); } }}
          className="flex items-center gap-1.5 text-xs border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 rounded-lg px-3 py-1.5 font-medium">
          <RefreshCw className={"h-3 w-3" + (loading ? " animate-spin" : "")} />
          {open ? "Close" : "Open Sync Panel"}
        </button>
      </div>

      {open && (
        <div className="border-t border-emerald-200 px-4 pb-4 pt-3 space-y-4">

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 w-fit">
            <button onClick={function() { setTab("import"); setError(null); }}
              className={"px-4 py-1.5 text-xs font-medium rounded-lg transition-colors " +
                (tab === "import" ? "bg-emerald-600 text-white" : "text-gray-500 hover:text-gray-700")}>
              Import from Xero
            </button>
            <button onClick={function() { setTab("export"); setError(null); }}
              className={"px-4 py-1.5 text-xs font-medium rounded-lg transition-colors " +
                (tab === "export" ? "bg-emerald-600 text-white" : "text-gray-500 hover:text-gray-700")}>
              Export to Xero
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          {/* ── IMPORT TAB ───────────────────────────────────────── */}
          {tab === "import" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  {loading ? "Loading Xero contacts\u2026" : contacts.length + " supplier contacts in Xero"}
                </p>
                <button onClick={fetchContacts} disabled={loading}
                  className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                  <RefreshCw className={"h-3 w-3" + (loading ? " animate-spin" : "")} /> Refresh
                </button>
              </div>

              {importResult && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                  <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                  Imported {importResult.imported} supplier{importResult.imported !== 1 ? "s" : ""}:
                  {" "}{importResult.results.map(function(r) { return r.name + " (" + r.action + ")"; }).join(", ")}
                </div>
              )}

              {contacts.length > 0 && (
                <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-gray-400 font-medium">
                        <th className="px-3 py-2 text-left w-8">
                          <input type="checkbox"
                            checked={contacts.filter(function(c) { return !linked[c.ContactID]; }).every(function(c) { return selected[c.ContactID]; })}
                            onChange={function(e) {
                              var sel = {};
                              contacts.forEach(function(c) {
                                if (!linked[c.ContactID]) sel[c.ContactID] = e.target.checked;
                              });
                              setSelected(function(prev) { return Object.assign({}, prev, sel); });
                            }}
                          />
                        </th>
                        <th className="px-3 py-2 text-left">Xero Contact</th>
                        <th className="px-3 py-2 text-left w-24">Email</th>
                        <th className="px-3 py-2 text-left">Match / Link to VetFlow Supplier</th>
                        <th className="px-3 py-2 text-left w-24">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {contacts.map(function(c) {
                        var cid = c.ContactID;
                        var isLinked = !!linked[cid];
                        var isAutoMatched = !!matched[cid];
                        var currentLink = linkMap[cid] || "";
                        return (
                          <tr key={cid} className={isLinked ? "opacity-60 bg-green-50/30" : ""}>
                            <td className="px-3 py-2">
                              <input type="checkbox"
                                checked={!!selected[cid]}
                                disabled={isLinked}
                                onChange={function(e) {
                                  setSelected(function(prev) { return Object.assign({}, prev, { [cid]: e.target.checked }); });
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{c.Name}</div>
                              {(c.Phones || []).length > 0 && (
                                <div className="text-gray-400">{c.Phones[0].PhoneNumber}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 truncate max-w-[120px]">{c.EmailAddress || "\u2014"}</td>
                            <td className="px-3 py-2">
                              {isLinked ? (
                                <span className="text-green-700 font-medium flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" /> Already linked
                                </span>
                              ) : (
                                <select
                                  value={currentLink}
                                  onChange={function(e) {
                                    setLinkMap(function(prev) { return Object.assign({}, prev, { [cid]: e.target.value }); });
                                  }}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-emerald-400 focus:outline-none">
                                  <option value="NEW">\u2795 Create as new VetFlow supplier</option>
                                  <option value="SKIP">\u23ed Skip</option>
                                  <option disabled>\u2500\u2500 Link to existing \u2500\u2500</option>
                                  {vfSuppliers.map(function(s) {
                                    return <option key={s.id} value={s.id}>{s.name} ({s.id})</option>;
                                  })}
                                </select>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {isLinked && (
                                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                  <Zap className="h-2.5 w-2.5" /> Synced
                                </span>
                              )}
                              {!isLinked && isAutoMatched && (
                                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                  Auto-match
                                </span>
                              )}
                              {!isLinked && !isAutoMatched && (
                                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                  Unmatched
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {contacts.length > 0 && (
                <div className="flex justify-end">
                  <button onClick={handleImport} disabled={importing || loading}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl text-sm font-medium">
                    {importing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    {importing ? "Importing\u2026" : "Import Selected"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── EXPORT TAB ───────────────────────────────────────── */}
          {tab === "export" && (
            <div className="space-y-3">
              <p className="text-xs text-gray-600">
                Select VetFlow suppliers to push to Xero. Already-linked suppliers will update their Xero contact.
              </p>

              {exportResult && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                  <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                  Exported {exportResult.exported} supplier{exportResult.exported !== 1 ? "s" : ""} to Xero.
                  {exportResult.results.filter(function(r) { return r.error; }).length > 0 && (
                    <span className="ml-1 text-red-600">
                      {exportResult.results.filter(function(r) { return r.error; }).length} failed.
                    </span>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-gray-200 overflow-hidden bg-white max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                    <tr className="text-gray-400 font-medium">
                      <th className="px-3 py-2 text-left w-8">
                        <input type="checkbox"
                          checked={vfSuppliers.length > 0 && vfSuppliers.every(function(s) { return exportSelected[s.id]; })}
                          onChange={function(e) {
                            var sel = {};
                            vfSuppliers.forEach(function(s) { sel[s.id] = e.target.checked; });
                            setExportSelected(sel);
                          }}
                        />
                      </th>
                      <th className="px-3 py-2 text-left">VetFlow Supplier</th>
                      <th className="px-3 py-2 text-left w-28">ID</th>
                      <th className="px-3 py-2 text-left w-32">Xero Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {vfSuppliers.map(function(s) {
                      return (
                        <tr key={s.id}>
                          <td className="px-3 py-2">
                            <input type="checkbox"
                              checked={!!exportSelected[s.id]}
                              onChange={function(e) {
                                setExportSelected(function(prev) { return Object.assign({}, prev, { [s.id]: e.target.checked }); });
                              }}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800">{s.name}</div>
                            {s.email && <div className="text-gray-400">{s.email}</div>}
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-500">{s.id}</td>
                          <td className="px-3 py-2">
                            {s.xero_contact_id ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                <Zap className="h-2.5 w-2.5" /> Linked
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                Not in Xero
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button onClick={handleExport} disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl text-sm font-medium">
                  {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  {exporting ? "Exporting\u2026" : "Export to Xero"}
                </button>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

// ── Match Inventory Modal ─────────────────────────────────────────────────────

const MatchInventoryModal = ({ supplier, onClose }) => {
  var [loading, setLoading] = useState(true);
  var [preview, setPreview] = useState(null);
  var [updateName, setUpdateName] = useState(true);
  var [applying, setApplying] = useState(false);
  var [done, setDone] = useState(null);
  var [error, setError] = useState(null);

  useEffect(function() {
    setLoading(true);
    apiFetch("/api/purchasing/suppliers/" + supplier.id + "/match-inventory", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preview: true })
    })
      .then(function(r) { return r.json(); })
      .then(function(d) { setPreview(d); })
      .catch(function(e) { setError(e.message); })
      .finally(function() { setLoading(false); });
  }, [supplier.id]);

  var handleApply = async function() {
    setApplying(true);
    setError(null);
    try {
      var res = await apiFetch("/api/purchasing/suppliers/" + supplier.id + "/match-inventory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preview: false, update_name: updateName })
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Apply failed");
      setDone(data);
    } catch(err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-emerald-600" />
            Match inventory to {supplier.name}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-auto flex-1 px-6 py-4 space-y-4 text-sm">
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Matching against</div>
            <div className="font-medium text-gray-800">{supplier.name}</div>
            {(supplier.aliases || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {(supplier.aliases || []).map(function(a, i) {
                  return <span key={i} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700">{a}</span>;
                })}
              </div>
            )}
            {(supplier.aliases || []).length === 0 && (
              <div className="text-xs text-gray-500 mt-1">No aliases set — only the supplier name will be used. Edit supplier to add aliases.</div>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-gray-500"><RefreshCw className="h-4 w-4 animate-spin" /> Searching inventory…</div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          {preview && !done && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                <div className="text-2xl font-semibold text-emerald-700">{preview.matched_items}</div>
                <div className="text-xs text-emerald-700">inventory item{preview.matched_items === 1 ? "" : "s"} match by name or alias</div>
              </div>

              {preview.samples && preview.samples.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Sample of matched items (up to 20)</div>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-auto">
                    {preview.samples.map(function(s) {
                      return (
                        <div key={s.id} className="px-3 py-2 text-xs flex items-center gap-2">
                          {s.product_code && <span className="font-mono text-gray-500">{s.product_code}</span>}
                          <span className="text-gray-800 truncate">{s.description}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {preview.matched_items > 0 && (
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={updateName}
                    onChange={function(e) { setUpdateName(e.target.checked); }}
                    className="mt-0.5" />
                  <span>Also overwrite each item's <code>supplier_name</code> with the canonical name "{supplier.name}" (recommended for clean reporting)</span>
                </label>
              )}
            </>
          )}

          {done && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 space-y-1">
              <div className="text-sm font-medium text-green-800 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Linked {done.updated_items} inventory item{done.updated_items === 1 ? "" : "s"} to {supplier.name}
              </div>
              <div className="text-xs text-green-700">{done.matched_items} matched in total ({done.matched_items - done.updated_items} were already linked).</div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            {done ? "Close" : "Cancel"}
          </button>
          {!done && preview && preview.matched_items > 0 && (
            <button onClick={handleApply} disabled={applying}
              className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {applying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              {applying ? "Applying…" : "Apply to " + preview.matched_items + " item" + (preview.matched_items === 1 ? "" : "s")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ── Supplier Database component ───────────────────────────────────────────────

const SupplierDatabase = ({ xeroStatus }) => {
  var [suppliers, setSuppliers] = useState([]);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(false);
  var [loadError, setLoadError] = useState(null);
  var [search, setSearch] = useState("");
  var [filterCategory, setFilterCategory] = useState("Active");
  var [modal, setModal] = useState(null); // null | { mode: "create" } | { mode: "edit", supplier }
  var [matchModal, setMatchModal] = useState(null); // null | supplier
  var [successMsg, setSuccessMsg] = useState(null);
  var [expanded, setExpanded] = useState(null);

  var load = useCallback(async function() {
    setLoading(true);
    setLoadError(null);
    try {
      var params = new URLSearchParams({ limit: 500 });
      if (search) params.set("q", search);
      if (filterCategory) params.set("category", filterCategory);
      var res = await apiFetch("/api/purchasing/suppliers?" + params.toString());
      var data = await res.json();
      setSuppliers(data.items || []);
      setTotal(data.total || 0);
    } catch(err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory]);

  useEffect(function() { load(); }, [load]);

  var handleSaved = function(saved) {
    setModal(null);
    var isEdit = suppliers.some(function(s) { return s.id === saved.id; });
    setSuccessMsg((isEdit ? "Updated" : "Created") + ": " + saved.name);
    setTimeout(function() { setSuccessMsg(null); }, 4000);
    load();
  };

  var handleDelete = async function(supplier) {
    if (!window.confirm("Delete \"" + supplier.name + "\"? This cannot be undone.")) return;
    try {
      await apiFetch("/api/purchasing/suppliers/" + supplier.id, { method: "DELETE" });
      load();
    } catch(err) {
      alert("Delete failed: " + err.message);
    }
  };

  return (
    <div className="space-y-4">
      {modal && (
        <SupplierFormModal
          initial={modal.mode === "edit" ? modal.supplier : null}
          onClose={function() { setModal(null); }}
          onSaved={handleSaved}
        />
      )}
      {matchModal && (
        <MatchInventoryModal
          supplier={matchModal}
          onClose={function() { setMatchModal(null); }}
        />
      )}

      {/* Xero sync panel */}
      <XeroSupplierSyncPanel xeroStatus={xeroStatus} onSyncComplete={load} />

      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 flex-1">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={function(e) { setSearch(e.target.value); }}
              placeholder="Search suppliers…"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
          </div>
          <select value={filterCategory} onChange={function(e) { setFilterCategory(e.target.value); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-500">
            <option value="">All categories</option>
            <option value="Active">Active</option>
            <option value="Non-inventory">Non-inventory</option>
            <option value="Inactive">Inactive</option>
          </select>
          <button onClick={load}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className={"h-4 w-4 text-gray-500" + (loading ? " animate-spin" : "")} />
          </button>
          <span className="text-sm text-gray-500">{total} supplier{total !== 1 ? "s" : ""}</span>
        </div>
        <button onClick={function() { setModal({ mode: "create" }); }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium">
          <Plus className="h-4 w-4" /> New Supplier
        </button>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm">
          <CheckCircle className="h-4 w-4 shrink-0" />{successMsg}
        </div>
      )}

      {loadError && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />{loadError}
        </div>
      )}

      {suppliers.length === 0 && !loading && !loadError ? (
        <div className="bg-white rounded-xl border border-gray-200 p-14 text-center text-gray-400">
          <Building className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No suppliers found</p>
          <p className="text-sm mt-1">Click "New Supplier" to add one, or use Invoice parsing to auto-create from invoices</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                <th className="px-4 py-3 text-left w-6"></th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left w-28">ID</th>
                <th className="px-4 py-3 text-left w-32">Postcode</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left w-36">Phone</th>
                <th className="px-4 py-3 text-right w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {suppliers.map(function(sup) {
                var isOpen = expanded === sup.id;
                return (
                  <React.Fragment key={sup.id}>
                    <tr className={"hover:bg-purple-50/30 transition-colors" + (isOpen ? " bg-purple-50/20" : "")}>
                      <td className="px-4 py-3">
                        <button onClick={function() { setExpanded(isOpen ? null : sup.id); }}
                          className="text-gray-400 hover:text-gray-600">
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{sup.name}</span>
                          {sup.category === "Non-inventory" && (
                            <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Non-inv</span>
                          )}
                          {sup.category === "Inactive" && (
                            <span className="inline-flex items-center text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">Inactive</span>
                          )}
                          {sup.xero_contact_id && (
                            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full" title={"Xero: " + sup.xero_contact_id}>
                              <Zap className="h-2.5 w-2.5" /> Xero
                            </span>
                          )}
                        </div>
                        {sup.address && (
                          <div className="text-xs text-gray-400 truncate max-w-xs mt-0.5">{sup.address.split(",")[0]}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{sup.id}</td>
                      <td className="px-4 py-3 text-gray-600">{sup.postcode || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {sup.email
                          ? <a href={"mailto:" + sup.email} className="hover:text-purple-600 hover:underline">{sup.email}</a>
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{sup.phone || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={function() { setMatchModal(sup); }}
                            className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Match inventory items to this supplier">
                            <Link2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={function() { setModal({ mode: "edit", supplier: sup }); }}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={function() { handleDelete(sup); }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Full Address</div>
                              <div className="text-gray-700 whitespace-pre-line">{sup.address || "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Contact</div>
                              <div className="space-y-1">
                                {sup.phone && <div className="text-gray-700">📞 {sup.phone}</div>}
                                {sup.email && <div><a href={"mailto:" + sup.email} className="text-purple-600 hover:underline">{sup.email}</a></div>}
                                {sup.website && <div><a href={sup.website} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline truncate block max-w-xs">{sup.website}</a></div>}
                                {!sup.phone && !sup.email && !sup.website && <div className="text-gray-400">—</div>}
                              </div>
                            </div>
                            {sup.notes && (
                              <div>
                                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Notes</div>
                                <div className="text-gray-700">{sup.notes}</div>
                              </div>
                            )}
                            {sup.created_at && (
                              <div>
                                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Added</div>
                                <div className="text-gray-500 text-xs">{sup.created_at.replace("T"," ").slice(0,16)}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Purchasing Analytics ──────────────────────────────────────────────────────
const PurchasingAnalytics = () => {
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState("overview");

  useEffect(function() {
    setLoading(true);
    Promise.all([
      apiFetch("/api/inventory/purchase-orders?limit=500").then(function(r) { return r.ok ? r.json() : { items: [] }; }),
      apiFetch("/api/purchasing/invoices?skip=0&limit=500").then(function(r) { return r.ok ? r.json() : { items: [] }; }),
      apiFetch("/api/inventory/items?limit=500").then(function(r) { return r.ok ? r.json() : { items: [] }; }),
    ]).then(function(results) {
      setPos(results[0].items || []);
      setInvoices(results[1].items || []);
      setItems(results[2].items || []);
    }).catch(function() {}).finally(function() { setLoading(false); });
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────────────

  var activePOs = pos.filter(function(p) { return !["received", "cancelled"].includes(p.status); });
  var outstandingPOs = pos.filter(function(p) { return !["received", "cancelled", "draft"].includes(p.status); });
  var draftPOs = pos.filter(function(p) { return p.status === "draft"; });

  var lowStockItems = items.filter(function(item) {
    return item.reorder_point > 0 && (item.qty_in_stock || 0) <= item.reorder_point;
  });
  var belowParItems = items.filter(function(item) {
    return item.par_level > 0 && (item.qty_in_stock || 0) < item.par_level;
  });
  var outOfStockItems = items.filter(function(item) { return (item.qty_in_stock || 0) <= 0; });

  var unpaidInvoices = invoices.filter(function(inv) { return !inv.paid; });
  var unpaidTotal = unpaidInvoices.reduce(function(sum, inv) {
    return sum + (parseFloat(inv.total) || 0);
  }, 0);
  var totalInvoiceSpend = invoices.reduce(function(sum, inv) {
    return sum + (parseFloat(inv.total) || 0);
  }, 0);

  // Spend by supplier (from invoices)
  var spendBySupplier = {};
  invoices.forEach(function(inv) {
    var name = inv.supplier || "Unknown";
    spendBySupplier[name] = (spendBySupplier[name] || 0) + (parseFloat(inv.total) || 0);
  });
  var spendBySupplierList = Object.entries(spendBySupplier)
    .map(function(e) { return { name: e[0], total: e[1] }; })
    .sort(function(a, b) { return b.total - a.total; })
    .slice(0, 10);
  var maxSupplierSpend = spendBySupplierList.length > 0 ? spendBySupplierList[0].total : 1;

  // Outstanding PO lines (sent/partial with unrecieved lines)
  var outstandingLines = [];
  outstandingPOs.forEach(function(po) {
    (po.lines || []).forEach(function(line) {
      if (!line.received) {
        outstandingLines.push({ po, line });
      }
    });
  });

  // Invoices linked to POs vs unlinked
  var linkedInvoices = invoices.filter(function(inv) { return inv.purchase_order_id && inv.purchase_order_id !== ""; });
  var unlinkedInvoices = invoices.filter(function(inv) { return !inv.purchase_order_id || inv.purchase_order_id === ""; });

  var fmt = function(n) {
    return "£" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  var reports = [
    { id: "overview",    label: "Overview" },
    { id: "outstanding", label: "Outstanding Orders" },
    { id: "lowstock",    label: "Low Stock" },
    { id: "spend",       label: "Spend by Supplier" },
    { id: "unpaid",      label: "Unpaid Invoices" },
    { id: "unlinked",    label: "Unlinked Invoices" },
  ];

  var statCard = function(label, value, sub, colorCls, Icon) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">{label}</p>
          <p className={"text-2xl font-bold " + colorCls}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={"w-11 h-11 rounded-xl flex items-center justify-center " + colorCls.replace("text-", "bg-").replace("-700", "-100").replace("-600", "-100")}>
          <Icon className={"h-5 w-5 " + colorCls} />
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
      <RefreshCw className="h-5 w-5 animate-spin" /> Loading analytics…
    </div>
  );

  return (
    <div className="space-y-5">

      {/* Report selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 flex flex-wrap gap-1">
        {reports.map(function(r) {
          return (
            <button key={r.id} onClick={function() { setActiveReport(r.id); }}
              className={"px-3 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                (activeReport === r.id ? "bg-purple-600 text-white" : "text-gray-500 hover:bg-gray-100")}>
              {r.label}
            </button>
          );
        })}
      </div>

      {/* ── Overview ── */}
      {activeReport === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statCard("Active POs", activePOs.length, draftPOs.length + " draft", "text-blue-700", ShoppingCart)}
            {statCard("Outstanding Deliveries", outstandingLines.length, outstandingPOs.length + " POs awaiting", "text-amber-600", Truck)}
            {statCard("Low / Out of Stock", lowStockItems.length, outOfStockItems.length + " out of stock", "text-red-600", AlertCircle)}
            {statCard("Unpaid Invoices", fmt(unpaidTotal), unpaidInvoices.length + " invoices", "text-orange-600", Receipt)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* PO status breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-purple-500" /> PO Status Breakdown
              </p>
              {[
                { status: "draft",     label: "Draft",        cls: "bg-gray-200" },
                { status: "submitted", label: "Submitted",    cls: "bg-amber-300" },
                { status: "approved",  label: "Approved",     cls: "bg-blue-300" },
                { status: "sent",      label: "Sent",         cls: "bg-purple-400" },
                { status: "partial",   label: "Part Received",cls: "bg-cyan-400" },
                { status: "received",  label: "Received",     cls: "bg-green-400" },
                { status: "cancelled", label: "Cancelled",    cls: "bg-red-300" },
              ].map(function(s) {
                var count = pos.filter(function(p) { return p.status === s.status; }).length;
                if (count === 0) return null;
                var pct = pos.length > 0 ? Math.round((count / pos.length) * 100) : 0;
                return (
                  <div key={s.status} className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-gray-500 w-28 shrink-0">{s.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className={"h-2 rounded-full " + s.cls} style={{ width: pct + "%" }} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
            {/* Invoice summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-purple-500" /> Invoice Summary
              </p>
              <div className="space-y-3">
                {[
                  { label: "Total invoices", val: invoices.length, cls: "text-gray-800" },
                  { label: "Total spend", val: fmt(totalInvoiceSpend), cls: "text-gray-800" },
                  { label: "Paid", val: (invoices.length - unpaidInvoices.length) + " · " + fmt(totalInvoiceSpend - unpaidTotal), cls: "text-green-700" },
                  { label: "Unpaid", val: unpaidInvoices.length + " · " + fmt(unpaidTotal), cls: "text-orange-600" },
                  { label: "Linked to PO", val: linkedInvoices.length, cls: "text-blue-700" },
                  { label: "Not linked to PO", val: unlinkedInvoices.length, cls: "text-gray-500" },
                ].map(function(row) {
                  return (
                    <div key={row.label} className="flex items-center justify-between text-xs border-b border-gray-50 pb-2">
                      <span className="text-gray-500">{row.label}</span>
                      <span className={"font-semibold " + row.cls}>{row.val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Outstanding Orders ── */}
      {activeReport === "outstanding" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-500" /> Outstanding Order Lines
            </p>
            <span className="text-xs text-gray-400">{outstandingLines.length} lines across {outstandingPOs.length} POs</span>
          </div>
          {outstandingLines.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">No outstanding lines — all deliveries received.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-400 font-medium">
                  <tr>
                    <th className="px-4 py-2 text-left">PO Number</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-left">Item</th>
                    <th className="px-4 py-2 text-left">Supplier Code</th>
                    <th className="px-4 py-2 text-right">Qty Ordered</th>
                    <th className="px-4 py-2 text-right">Qty Received</th>
                    <th className="px-4 py-2 text-right">Unit Price</th>
                    <th className="px-4 py-2 text-center">PO Status</th>
                    <th className="px-4 py-2 text-left">PO Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {outstandingLines.map(function(entry, i) {
                    var { po, line } = entry;
                    var statusCls = { draft: "bg-gray-100 text-gray-600", submitted: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", sent: "bg-purple-100 text-purple-700", partial: "bg-cyan-100 text-cyan-700", pending: "bg-orange-100 text-orange-700" }[po.status] || "bg-gray-100 text-gray-600";
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono font-semibold text-gray-800">{po.po_number}</td>
                        <td className="px-4 py-2 text-gray-600">{po.supplier_name}</td>
                        <td className="px-4 py-2 text-gray-800">{line.inventory_item_description}</td>
                        <td className="px-4 py-2 font-mono text-gray-500">{line.supplier_code || "—"}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{line.qty_ordered}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{line.qty_received || 0}</td>
                        <td className="px-4 py-2 text-right text-gray-600">{line.unit_price ? "£" + line.unit_price : "—"}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={"px-2 py-0.5 rounded-full font-medium text-xs " + statusCls}>{po.status}</span>
                        </td>
                        <td className="px-4 py-2 text-gray-400">{po.created_at ? po.created_at.slice(0, 10) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Low Stock ── */}
      {activeReport === "lowstock" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {statCard("At/Below Reorder Point", lowStockItems.length, "trigger a PO", "text-red-600", AlertCircle)}
            {statCard("Below Par Level", belowParItems.length, "below minimum target", "text-amber-600", AlertCircle)}
            {statCard("Out of Stock", outOfStockItems.length, "zero units", "text-red-700", AlertCircle)}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" /> Items Needing Reorder
              </p>
            </div>
            {lowStockItems.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">All items are above reorder point.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-400 font-medium">
                    <tr>
                      <th className="px-4 py-2 text-left">Item</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-left">Default Supplier</th>
                      <th className="px-4 py-2 text-right">In Stock</th>
                      <th className="px-4 py-2 text-right">Reorder At</th>
                      <th className="px-4 py-2 text-right">Par Level</th>
                      <th className="px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {lowStockItems.map(function(item) {
                      var defSup = (item.suppliers || []).find(function(s) { return s.is_default; }) || (item.suppliers || [])[0] || null;
                      var isOut = (item.qty_in_stock || 0) <= 0;
                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-800">{item.description}</div>
                            {item.generic_name && <div className="text-gray-400">{item.generic_name}</div>}
                          </td>
                          <td className="px-4 py-2 text-gray-500">{item.item_type || "—"}</td>
                          <td className="px-4 py-2 text-gray-600">{defSup ? defSup.supplier_name : "—"}</td>
                          <td className={"px-4 py-2 text-right font-semibold " + (isOut ? "text-red-600" : "text-amber-600")}>{item.qty_in_stock ?? 0}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{item.reorder_point}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{item.par_level}</td>
                          <td className="px-4 py-2 text-center">
                            {isOut
                              ? <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Out of Stock</span>
                              : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Low</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Spend by Supplier ── */}
      {activeReport === "spend" && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-purple-500" /> Invoice Spend by Supplier (top 10)
          </p>
          {spendBySupplierList.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No invoice data yet.</p>
          ) : (
            <div className="space-y-3">
              {spendBySupplierList.map(function(entry) {
                var pct = maxSupplierSpend > 0 ? (entry.total / maxSupplierSpend) * 100 : 0;
                var invCount = invoices.filter(function(inv) { return inv.supplier === entry.name; }).length;
                return (
                  <div key={entry.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700 truncate max-w-xs">{entry.name}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-gray-400">{invCount} invoice{invCount !== 1 ? "s" : ""}</span>
                        <span className="text-xs font-bold text-gray-800">{fmt(entry.total)}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-purple-500" style={{ width: pct + "%" }} />
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-400 mr-2">Total all invoices:</span>
                <span className="text-xs font-bold text-gray-800">{fmt(totalInvoiceSpend)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Unpaid Invoices ── */}
      {activeReport === "unpaid" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-orange-500" /> Unpaid Invoices
            </p>
            <span className="text-xs font-bold text-orange-600">{unpaidInvoices.length} invoices · {fmt(unpaidTotal)}</span>
          </div>
          {unpaidInvoices.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">All invoices are paid.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-400 font-medium">
                  <tr>
                    <th className="px-4 py-2 text-left">Invoice ID</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-left">Invoice No.</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Due Date</th>
                    <th className="px-4 py-2 text-left">Hub</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-left">Linked PO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {unpaidInvoices.map(function(inv) {
                    return (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-gray-500">{inv.id}</td>
                        <td className="px-4 py-2 font-medium text-gray-800">{inv.supplier || "—"}</td>
                        <td className="px-4 py-2 text-gray-600">{inv.invoice_number || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">{inv.invoice_date || "—"}</td>
                        <td className={"px-4 py-2 " + (inv.due_date && inv.due_date < new Date().toISOString().slice(0,10) ? "text-red-600 font-semibold" : "text-gray-500")}>{inv.due_date || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">{inv.hub_name || "—"}</td>
                        <td className="px-4 py-2 text-right font-bold text-gray-800">{inv.currency || "£"}{inv.total || "0.00"}</td>
                        <td className="px-4 py-2 text-blue-600">{inv.purchase_order_number || <span className="text-gray-300">none</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr>
                    <td colSpan={6} className="px-4 py-2 text-xs font-semibold text-gray-500 text-right">Total Outstanding:</td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-orange-600">{fmt(unpaidTotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Unlinked Invoices ── */}
      {activeReport === "unlinked" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Unlink className="h-4 w-4 text-gray-500" /> Invoices Not Linked to a Purchase Order
            </p>
            <span className="text-xs text-gray-400">{unlinkedInvoices.length} of {invoices.length} invoices</span>
          </div>
          {unlinkedInvoices.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">All invoices are linked to a PO.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-400 font-medium">
                  <tr>
                    <th className="px-4 py-2 text-left">Invoice ID</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-left">Invoice No.</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Hub</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-center">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {unlinkedInvoices.map(function(inv) {
                    return (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-gray-500">{inv.id}</td>
                        <td className="px-4 py-2 font-medium text-gray-800">{inv.supplier || "—"}</td>
                        <td className="px-4 py-2 text-gray-600">{inv.invoice_number || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">{inv.invoice_date || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">{inv.hub_name || "—"}</td>
                        <td className="px-4 py-2 text-right font-bold text-gray-800">{inv.currency || "£"}{inv.total || "0.00"}</td>
                        <td className="px-4 py-2 text-center">
                          {inv.paid
                            ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Paid</span>
                            : <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Unpaid</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ── Main Purchasing Component ─────────────────────────────────────────────────
const Purchasing = ({ navigateTo }) => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("inventory");
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceRefresh, setInvoiceRefresh] = useState(0);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [dnInitialPo, setDnInitialPo] = useState(null);
  const uploadPanelRef = useRef();
  const { status: xeroStatus, check: checkXero, disconnect: disconnectXero } = useXeroStatus();

  // Handle Xero OAuth callback redirect — ?xero=connected or ?xero=error
  useEffect(function() {
    var params = new URLSearchParams(window.location.search);
    var xeroParam = params.get("xero");
    if (xeroParam) {
      setActiveTab("invoices");
      if (xeroParam === "connected") checkXero();
      // Clean up the URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleEditInvoice = function(inv) {
    setEditingInvoice(inv);
    if (uploadPanelRef.current) {
      uploadPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLogout = function() { logout(); navigateTo("home"); };

  const handleCreateDN = function(po) {
    setDnInitialPo(po);
    setActiveTab("delivery-notes");
  };

  const tabs = [
    { id: "inventory", label: "Inventory", icon: Package },
    { id: "orders", label: "Purchase Orders", icon: ShoppingCart },
    { id: "delivery-notes", label: "Delivery Notes", icon: Truck },
    { id: "invoices", label: "Invoices", icon: Receipt },
    { id: "suppliers", label: "Suppliers", icon: Building },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  const getStatusColor = function(status) {
    var map = { delivered: "bg-green-100 text-green-700", shipped: "bg-blue-100 text-blue-700", processing: "bg-yellow-100 text-yellow-700", pending: "bg-orange-100 text-orange-700" };
    return map[status] || "bg-gray-100 text-gray-700";
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button onClick={function() { navigateTo("staff-portal"); }} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Purchasing Control</h1>
                <p className="text-sm text-gray-500">Manage supplies, medications, and equipment procurement</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg">
                <Plus className="h-4 w-4" /><span>New Order</span>
              </button>
              <button onClick={handleLogout} className="px-4 py-2 text-gray-700 hover:text-gray-900">Logout</button>
              <UserMenu navigateTo={navigateTo} />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex space-x-1 px-4 pt-4 overflow-x-auto">
            {tabs.map(function(tab) {
              var Icon = tab.icon;
              return (
                <button key={tab.id} onClick={function() { setActiveTab(tab.id); }}
                  className={"flex items-center gap-1.5 pb-4 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap " + (activeTab === tab.id ? "border-purple-600 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700")}>
                  <Icon className="h-3.5 w-3.5" />{tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === "suppliers" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input type="text" placeholder={"Search " + activeTab + "..."} value={searchTerm}
                    onChange={function(e) { setSearchTerm(e.target.value); }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
              <div className="flex space-x-3">
                <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"><Filter className="h-4 w-4" /><span>Filter</span></button>
                <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="h-4 w-4" /><span>Export</span></button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "invoices" && (
          <div className="space-y-6">
            <div ref={uploadPanelRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                {editingInvoice
                  ? <><Edit className="h-4 w-4 text-purple-600" /> Editing Invoice {editingInvoice.id}</>
                  : <><Upload className="h-4 w-4 text-purple-600" /> Upload and Parse Invoice</>}
              </h2>
              <InvoiceUploadPanel
                key={editingInvoice ? editingInvoice.id : "new"}
                editInvoice={editingInvoice}
                onCancelEdit={function() { setEditingInvoice(null); }}
                onSaved={function() {
                  setEditingInvoice(null);
                  setInvoiceRefresh(function(r) { return r + 1; });
                }}
              />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-600" /> Stored Invoices
                </h2>
              </div>
              <div className="mb-4">
                <XeroConnectBanner status={xeroStatus} onDisconnect={disconnectXero} />
              </div>
              <InvoicesList
                refresh={invoiceRefresh}
                onEditInvoice={handleEditInvoice}
                xeroStatus={xeroStatus}
                onXeroPost={function() { setInvoiceRefresh(function(r) { return r + 1; }); }}
              />
            </div>
          </div>
        )}
        {activeTab === "orders" && (
          <div className="-mx-4 sm:-mx-6 lg:-mx-8">
            <PurchaseOrders onCreateDN={handleCreateDN} />
          </div>
        )}
        {activeTab === "delivery-notes" && (
          <DeliveryNotes
            key={dnInitialPo ? dnInitialPo.id : "all"}
            initialPoId={dnInitialPo ? dnInitialPo.id : null}
            initialPoNumber={dnInitialPo ? dnInitialPo.po_number : ""}
          />
        )}
        {activeTab === "inventory" && (
          <div className="-mx-4 sm:-mx-6 lg:-mx-8">
            <Inventory />
          </div>
        )}
        {activeTab === "suppliers" && <SupplierDatabase xeroStatus={xeroStatus} />}
        {activeTab === "analytics" && <PurchasingAnalytics />}
      </div>
    </div>
  );
};

export default Purchasing;